/**
 * Leisure Lounges — Custom Curtains, S-Wave Sheers & Roman Blinds 3D WebGL PBR + Window AR Studio
 * - Made-to-Measure Curtains, S-Wave Sheers & Roman Blinds Pricing Engine
 * - Made-to-Measure Architectural Tracking & Motorisation Catalogue
 * - Zepel Fabrics & Mokum Designer Drapery Collections
 * - Real 4K Scanned PBR Textures (Diffuse, Normal, Roughness, Alpha, Specular)
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.LLCurtainStudio = factory();
  }
})(typeof globalThis !== 'undefined' ? globalThis : window, function () {
  'use strict';

  const STUDIO_RATE_FACTOR = 1.925;
  const STUDIO_EX_GST_FACTOR = 1.75;
  const BOX_FREIGHT_BASE = 50.0;
  const QUOTE_FORM_ENDPOINT = 'https://api.hsforms.com/submissions/v3/integration/submit/22127760/34bd9087-ea22-4802-a797-84ba15e8ba5f';

  // 21 width brackets: 1000mm to 6000mm in 250mm increments
  const TRACK_WIDTH_BRACKETS = [
    1000, 1250, 1500, 1750, 2000, 2250, 2500, 2750, 3000, 3250,
    3500, 3750, 4000, 4250, 4500, 4750, 5000, 5250, 5500, 5750, 6000
  ];

  // Architectural Track Tables — Customer-facing model names
  const TRACK_TABLES = {
    styletrack: {
      id: 'styletrack',
      name: 'Styletrack',
      subtitle: 'Hand-drawn heavy duty extruded aluminium track',
      finishes: [
        { id: 'white', name: 'White Birch', hex: '#F3F2EE', surchargePct: 0, metalness: 0.15, roughness: 0.36 },
        { id: 'black', name: 'Matt Black', hex: '#232426', surchargePct: 0, metalness: 0.28, roughness: 0.42 },
        { id: 'silver', name: 'Anodised Silver', hex: '#B9BDC2', surchargePct: 0, metalness: 0.78, roughness: 0.28 },
        { id: 'champagne', name: 'Champagne', hex: '#C7B89E', surchargePct: 0, metalness: 0.72, roughness: 0.30 },
        { id: 'chrome', name: 'Polished Chrome (+10%)', hex: '#D8DDE2', surchargePct: 0.10, metalness: 0.94, roughness: 0.12 },
        { id: 'gold', name: 'Brushed Gold (+10%)', hex: '#C5A059', surchargePct: 0.10, metalness: 0.86, roughness: 0.24 }
      ],
      bendBaseRate: 30.0,
      prices: {
        single_top_glider: [26.50, 26.50, 26.50, 26.50, 26.50, 28.05, 29.62, 31.17, 32.73, 35.15, 37.56, 39.13, 40.68, 42.24, 43.80, 47.12, 50.45, 52.00, 53.57, 56.10, 58.64],
        single_face_glider: [34.35, 34.35, 34.35, 34.35, 34.35, 35.91, 37.47, 40.29, 43.11, 45.52, 47.95, 50.77, 53.60, 55.15, 56.72, 60.04, 63.36, 66.19, 69.01, 74.30, 79.59],
        single_top_wave: [53.77, 57.58, 61.36, 65.17, 68.96, 74.30, 79.68, 85.01, 90.37, 96.60, 102.80, 108.16, 113.52, 118.87, 124.21, 131.34, 138.49, 143.82, 149.19, 152.06, 154.94],
        single_face_wave: [61.62, 65.43, 69.21, 73.02, 76.81, 82.15, 87.53, 94.13, 100.75, 106.97, 113.19, 119.80, 126.44, 131.78, 137.13, 144.26, 151.40, 158.01, 164.63, 170.26, 175.89],
        double_top_glider: [52.99, 52.99, 52.99, 52.99, 52.99, 56.11, 59.23, 62.35, 65.47, 70.29, 75.13, 78.25, 81.36, 84.48, 87.60, 94.24, 100.90, 104.01, 107.14, 112.22, 117.29],
        double_face_glider: [68.70, 68.70, 68.70, 68.70, 68.70, 71.82, 74.94, 80.58, 86.22, 91.04, 95.90, 101.54, 107.20, 110.30, 113.44, 120.08, 126.72, 132.38, 138.02, 148.60, 159.18],
        double_top_wave_glider: [88.13, 91.93, 95.72, 99.52, 103.32, 110.22, 117.15, 125.30, 133.48, 142.12, 150.76, 158.93, 167.12, 174.02, 180.93, 191.39, 201.85, 210.02, 218.20, 226.36, 213.59],
        double_face_wave_glider: [103.84, 107.64, 111.43, 115.23, 119.03, 125.93, 132.86, 143.53, 154.23, 162.87, 171.53, 182.22, 192.96, 199.84, 206.77, 217.23, 227.67, 238.39, 249.08, 262.74, 276.40]
      }
    },
    snapfit: {
      id: 'snapfit',
      name: 'Snap Fit Track',
      subtitle: 'Concealed snap-bracket architectural S-Wave track',
      finishes: [
        { id: 'white', name: 'White', hex: '#F4F4F1', surchargePct: 0, metalness: 0.14, roughness: 0.35 },
        { id: 'black', name: 'Matt Black', hex: '#202123', surchargePct: 0, metalness: 0.28, roughness: 0.42 },
        { id: 'silver', name: 'Anodised Silver', hex: '#BCC0C5', surchargePct: 0, metalness: 0.78, roughness: 0.28 }
      ],
      bendBaseRate: 30.0,
      prices: {
        single_top_wave: [43.49, 47.62, 51.78, 55.92, 60.05, 65.58, 71.10, 76.62, 82.13, 88.53, 94.92, 100.45, 105.97, 111.50, 117.01, 124.71, 132.43, 137.95, 143.48, 140.03, 136.58],
        single_face_wave: [53.15, 57.28, 61.44, 65.58, 69.71, 75.24, 80.76, 88.70, 96.63, 103.03, 109.42, 117.36, 125.30, 130.83, 136.34, 144.04, 151.76, 159.70, 167.64, 166.61, 165.58],
        double_top_wave_glider: [93.28, 101.56, 109.86, 118.15, 126.43, 137.47, 148.51, 162.89, 177.27, 190.06, 202.86, 213.90, 224.96, 236.01, 247.05, 265.79, 284.53, 295.58, 306.63, 306.63, 306.63],
        double_face_wave_glider: [112.61, 120.89, 129.20, 137.48, 145.75, 156.81, 167.85, 187.05, 206.27, 219.07, 231.86, 247.74, 263.62, 274.67, 285.71, 304.46, 323.20, 339.08, 354.96, 356.57, 358.17]
      }
    },
    commercial: {
      id: 'commercial',
      name: 'Commercial Track',
      subtitle: 'Heavy commercial S-Fold & sheer/blockout dual tracking',
      finishes: [
        { id: 'white', name: 'White', hex: '#F3F3F0', surchargePct: 0, metalness: 0.15, roughness: 0.36 },
        { id: 'black', name: 'Matt Black', hex: '#1F2022', surchargePct: 0, metalness: 0.28, roughness: 0.42 },
        { id: 'silver', name: 'Silver', hex: '#B8BDC2', surchargePct: 0, metalness: 0.78, roughness: 0.28 }
      ],
      bendBaseRate: 30.0,
      prices: {
        single_top_wave: [47.90, 50.90, 55.37, 58.65, 65.58, 69.92, 74.64, 79.13, 86.44, 90.98, 96.76, 101.14, 109.85, 114.22, 118.59, 124.22, 134.16, 138.94, 143.10, 146.84, 150.61],
        single_face_wave: [57.83, 60.83, 65.29, 68.58, 78.82, 83.16, 87.89, 92.38, 102.99, 107.53, 113.31, 117.70, 129.71, 134.09, 138.46, 144.08, 157.33, 162.12, 166.28, 171.69, 177.10],
        double_top_wave_glider: [75.50, 78.63, 84.56, 88.00, 99.69, 104.22, 111.50, 116.17, 126.15, 133.24, 139.21, 143.79, 159.40, 163.98, 168.55, 176.72, 193.66, 198.65, 203.00, 209.45, 215.92],
        double_face_wave_glider: [93.29, 96.42, 102.35, 105.79, 123.43, 127.95, 135.24, 139.91, 155.83, 162.92, 168.89, 173.47, 195.01, 199.58, 204.16, 212.33, 235.20, 240.18, 244.54, 253.94, 263.35]
      }
    },
    designer25: {
      id: 'designer25',
      name: 'Designer 25 Rod',
      subtitle: '25mm architectural decorator rod with S-Wave / glider channel',
      finishes: [
        { id: 'black', name: 'Matt Black', hex: '#202123', surchargePct: 0, metalness: 0.34, roughness: 0.38 },
        { id: 'white', name: 'Warm White', hex: '#F3F1EC', surchargePct: 0, metalness: 0.16, roughness: 0.34 },
        { id: 'silver', name: 'Brushed Silver', hex: '#B9BEC4', surchargePct: 0, metalness: 0.84, roughness: 0.24 },
        { id: 'champagne', name: 'Champagne Bronze', hex: '#C2B197', surchargePct: 0, metalness: 0.78, roughness: 0.28 }
      ],
      bendBaseRate: 30.0,
      prices: {
        single_face_wave: [60.97, 65.11, 69.26, 73.41, 77.54, 83.07, 88.59, 98.95, 109.30, 115.70, 122.09, 127.62, 133.14, 138.67, 144.19, 156.74, 169.28, 179.64, 189.99, 200.34, 210.69],
        double_face_wave_glider: [95.32, 99.46, 103.61, 107.76, 111.89, 118.98, 126.06, 139.24, 152.41, 161.22, 170.04, 178.39, 186.74, 193.82, 200.91, 216.78, 232.64, 245.83, 259.00, 274.64, 290.28]
      }
    },
    saber_motorised: {
      id: 'saber_motorised',
      name: 'Saber Motorised Track',
      subtitle: 'Whisper-quiet motorised track with remote & smart home control',
      finishes: [
        { id: 'white', name: 'Pure White', hex: '#F5F5F2', surchargePct: 0, metalness: 0.18, roughness: 0.32 },
        { id: 'black', name: 'Architectural Black', hex: '#1F2022', surchargePct: 0, metalness: 0.32, roughness: 0.38 },
        { id: 'silver', name: 'Anodised Silver', hex: '#BCC0C5', surchargePct: 0, metalness: 0.80, roughness: 0.26 }
      ],
      bendBaseRate: 90.0,
      prices: {
        single_top_glider: [714.72, 725.24, 735.77, 746.29, 756.82, 767.34, 777.87, 788.39, 798.92, 809.44, 819.96, 830.49, 841.01, 851.54, 862.06, 872.59, 883.11, 893.64, 904.16, 914.69, 925.17],
        single_top_wave: [829.95, 841.65, 853.35, 865.05, 876.75, 888.45, 900.15, 911.85, 923.54, 935.24, 946.94, 958.64, 970.34, 982.04, 993.74, 1005.44, 1017.14, 1028.84, 1040.54, 1052.24, 1063.94],
        double_top_wave_glider: [918.08, 933.58, 949.07, 964.57, 980.07, 998.67, 1017.30, 1037.15, 1057.02, 1077.36, 1097.70, 1117.57, 1137.46, 1156.06, 1174.67, 1196.83, 1218.99, 1238.86, 1258.74, 1278.60, 1277.53]
      }
    }
  };

  // 18 Verified Zepel (March 2026) & Mokum / James Dunlop (Oct 2026) Fabrics + Real Twinbru PBR Scan Tile Index (0..8)
  const FABRIC_RANGES = [
    // Continuous Wide-Width Sheers (300cm - 330cm)
    {
      id: 'zepel-flo-ii',
      supplier: 'Zepel Fabrics',
      name: 'Flo II Wide Sheer',
      category: 'sheer',
      rollWidthCm: 300,
      ratePerMetre: 18.00,
      velvetSurcharge: false,
      pbrTile: 0, // voile-sheer (Twinbru 1330082)
      repeatM: 0.26,
      sheerOpacity: 0.42,
      roughness: 0.76,
      sheen: 0.35,
      composition: '100% Pes FR Wide-Width Voile (300cm)',
      swatches: [
        { id: 'snow', name: 'Snow', hex: '#F6F5F0' },
        { id: 'chalk', name: 'Chalk', hex: '#EAE6DD' },
        { id: 'oat', name: 'Oatmeal', hex: '#D8CFC2' },
        { id: 'pebble', name: 'Pebble', hex: '#B9B2A7' },
        { id: 'smoke', name: 'Smoke', hex: '#7E8082' }
      ]
    },
    {
      id: 'zepel-mode-fr',
      supplier: 'Zepel Fabrics',
      name: 'Mode FR Architectural Sheer',
      category: 'sheer',
      rollWidthCm: 300,
      ratePerMetre: 18.00,
      velvetSurcharge: false,
      pbrTile: 0,
      repeatM: 0.24,
      sheerOpacity: 0.45,
      roughness: 0.74,
      sheen: 0.38,
      composition: '100% Flame Retardant Polyester (300cm)',
      swatches: [
        { id: 'ivory', name: 'Ivory', hex: '#F3EFE6' },
        { id: 'alabaster', name: 'Alabaster', hex: '#E5DFD3' },
        { id: 'taupe', name: 'Warm Taupe', hex: '#C2B8A8' },
        { id: 'silver', name: 'Silver Mist', hex: '#C6C8CA' }
      ]
    },
    {
      id: 'zepel-graceful',
      supplier: 'Zepel Fabrics',
      name: 'Graceful Linen-Look Sheer',
      category: 'sheer',
      rollWidthCm: 330,
      ratePerMetre: 28.00,
      velvetSurcharge: false,
      pbrTile: 1, // linen-sheer (Twinbru 2227706)
      repeatM: 0.20,
      sheerOpacity: 0.48,
      roughness: 0.82,
      sheen: 0.42,
      composition: 'Extra-Wide 330cm Soft Linen-Look Slub Sheer',
      swatches: [
        { id: 'milk', name: 'Milk', hex: '#F4F1EA' },
        { id: 'ecru', name: 'Ecru', hex: '#E3DBC9' },
        { id: 'flax', name: 'Natural Flax', hex: '#CBBFA8' },
        { id: 'pumice', name: 'Pumice', hex: '#B4ACA0' },
        { id: 'charcoal', name: 'Soft Charcoal', hex: '#585A5C' }
      ]
    },
    {
      id: 'zepel-cirrus',
      supplier: 'Zepel Fabrics',
      name: 'Cirrus Textured Sheer',
      category: 'sheer',
      rollWidthCm: 330,
      ratePerMetre: 38.00,
      velvetSurcharge: false,
      pbrTile: 1,
      repeatM: 0.22,
      sheerOpacity: 0.50,
      roughness: 0.84,
      sheen: 0.40,
      composition: 'Extra-Wide 330cm Organic Open-Weave Drapery Sheer',
      swatches: [
        { id: 'cloud', name: 'Cloud', hex: '#F2EFE9' },
        { id: 'parchment', name: 'Parchment', hex: '#E1D8C7' },
        { id: 'sandstone', name: 'Sandstone', hex: '#C8B9A3' },
        { id: 'ash', name: 'Warm Ash', hex: '#9D978E' }
      ]
    },
    {
      id: 'zepel-beauvoir',
      supplier: 'Zepel Fabrics',
      name: 'Beauvoir Bouclé Sheer',
      category: 'sheer',
      rollWidthCm: 304,
      ratePerMetre: 45.00,
      velvetSurcharge: false,
      pbrTile: 2, // boucle-sheer (Twinbru 100398)
      repeatM: 0.30,
      sheerOpacity: 0.55,
      roughness: 0.88,
      sheen: 0.45,
      composition: '304cm Heavy Textural Bouclé Yarn Sheer',
      swatches: [
        { id: 'pearl', name: 'Pearl', hex: '#F0ECE3' },
        { id: 'biscuit', name: 'Biscuit', hex: '#DACBB4' },
        { id: 'driftwood', name: 'Driftwood', hex: '#B5A795' },
        { id: 'slate', name: 'Slate', hex: '#6F7275' }
      ]
    },
    {
      id: 'zepel-iconic',
      supplier: 'Zepel Fabrics',
      name: 'Iconic Belgian-Blend Sheer',
      category: 'sheer',
      rollWidthCm: 305,
      ratePerMetre: 55.00,
      velvetSurcharge: false,
      pbrTile: 2,
      repeatM: 0.28,
      sheerOpacity: 0.56,
      roughness: 0.85,
      sheen: 0.44,
      composition: '305cm Linen / Polyester Weighted Drapery Sheer',
      swatches: [
        { id: 'blanc', name: 'Blanc', hex: '#F4F2EC' },
        { id: 'linen', name: 'Raw Linen', hex: '#D8CCB8' },
        { id: 'putty', name: 'Putty', hex: '#BFB4A3' },
        { id: 'graphite', name: 'Graphite', hex: '#4F5256' }
      ]
    },
    {
      id: 'zepel-dakota-plain',
      supplier: 'Zepel Fabrics',
      name: 'Dakota Plain Heavy Sheer',
      category: 'sheer',
      rollWidthCm: 304,
      ratePerMetre: 65.00,
      velvetSurcharge: false,
      pbrTile: 3, // architectural-sheer (Twinbru 81904)
      repeatM: 0.26,
      sheerOpacity: 0.62,
      roughness: 0.80,
      sheen: 0.46,
      composition: '304cm Semi-Opaque Architectural Drapery Sheer',
      swatches: [
        { id: 'whisper', name: 'Whisper', hex: '#EFECE5' },
        { id: 'dune', name: 'Dune', hex: '#D6C8B2' },
        { id: 'stone', name: 'Stone', hex: '#ADA395' },
        { id: 'onyx', name: 'Onyx', hex: '#3D3F42' }
      ]
    },
    {
      id: 'mokum-lino',
      supplier: 'Mokum',
      name: 'Mokum Lino Pure Linen Sheer',
      category: 'sheer',
      rollWidthCm: 300,
      ratePerMetre: 95.00,
      velvetSurcharge: false,
      pbrTile: 1,
      repeatM: 0.18,
      sheerOpacity: 0.50,
      roughness: 0.86,
      sheen: 0.42,
      composition: '100% Italian-Woven Linen Wide Sheer (300cm)',
      swatches: [
        { id: 'gesso', name: 'Gesso White', hex: '#F3F0E8' },
        { id: 'natural', name: 'Natural Linen', hex: '#D7C8B2' },
        { id: 'travertine', name: 'Travertine', hex: '#C3B59F' },
        { id: 'bronze', name: 'Aged Bronze', hex: '#7C7062' }
      ]
    },

    // Wide-Width Continuous Blockout & Drapery (300cm - 320cm)
    {
      id: 'zepel-equinox',
      supplier: 'Zepel Fabrics',
      name: 'Equinox 3-Pass Blackout',
      category: 'blockout',
      rollWidthCm: 320,
      ratePerMetre: 22.00,
      velvetSurcharge: false,
      pbrTile: 6, // dimout-blackout (Twinbru 68457)
      repeatM: 0.24,
      roughness: 0.72,
      sheen: 0.48,
      composition: '320cm 100% Blockout Thermal & Acoustic Drapery',
      swatches: [
        { id: 'arctic', name: 'Arctic White', hex: '#EFECE6' },
        { id: 'champagne', name: 'Champagne', hex: '#DDD3C2' },
        { id: 'greige', name: 'Warm Greige', hex: '#B8AFA2' },
        { id: 'steel', name: 'Steel Grey', hex: '#76797D' },
        { id: 'cinema-black', name: 'Cinema Pitch Black', hex: '#1F2023' }
      ]
    },
    {
      id: 'zepel-aspro',
      supplier: 'Zepel Fabrics',
      name: 'Aspro Textured Blackout',
      category: 'blockout',
      rollWidthCm: 300,
      ratePerMetre: 28.00,
      velvetSurcharge: false,
      pbrTile: 6,
      repeatM: 0.25,
      roughness: 0.75,
      sheen: 0.46,
      composition: '300cm Woven Texture 100% Blockout Coating',
      swatches: [
        { id: 'ivory', name: 'Ivory', hex: '#ECE7DD' },
        { id: 'sesame', name: 'Sesame', hex: '#CFC3B0' },
        { id: 'fossil', name: 'Fossil', hex: '#9E9589' },
        { id: 'storm', name: 'Storm', hex: '#4B4F54' },
        { id: 'midnight', name: 'Midnight Navy', hex: '#232B38' }
      ]
    },
    {
      id: 'zepel-retreat',
      supplier: 'Zepel Fabrics',
      name: 'Retreat Soft-Drape Blackout',
      category: 'blockout',
      rollWidthCm: 300,
      ratePerMetre: 45.00,
      velvetSurcharge: false,
      pbrTile: 6,
      repeatM: 0.24,
      roughness: 0.68,
      sheen: 0.56,
      composition: '300cm Supple Siliconised 3-Pass Acoustic Blackout',
      swatches: [
        { id: 'alabaster', name: 'Alabaster', hex: '#EAE5DB' },
        { id: 'latte', name: 'Latte', hex: '#C6B7A3' },
        { id: 'mushroom', name: 'Mushroom', hex: '#9A8F82' },
        { id: 'eucalyptus', name: 'Eucalyptus', hex: '#6D7A6E' },
        { id: 'obsidian', name: 'Obsidian Theatre', hex: '#1C1D20' }
      ]
    },
    {
      id: 'zepel-motion',
      supplier: 'Zepel Fabrics',
      name: 'Motion Architectural Drapery',
      category: 'drapery',
      rollWidthCm: 310,
      ratePerMetre: 60.00,
      velvetSurcharge: false,
      pbrTile: 4, // linen-drapery (Twinbru 251846)
      repeatM: 0.30,
      roughness: 0.80,
      sheen: 0.52,
      composition: '310cm Heavy Woven Linen-Blend Room Darkening Drapery',
      swatches: [
        { id: 'chalk', name: 'Chalk', hex: '#ECE8DF' },
        { id: 'oat', name: 'Oatmeal', hex: '#D2C4B0' },
        { id: 'taupe', name: 'Warm Taupe', hex: '#A89B89' },
        { id: 'anthracite', name: 'Anthracite', hex: '#3B3E42' }
      ]
    },
    {
      id: 'zepel-ultra-dim',
      supplier: 'Zepel Fabrics',
      name: 'Ultra Dim Acoustic Dimout',
      category: 'blockout',
      rollWidthCm: 302,
      ratePerMetre: 65.00,
      velvetSurcharge: false,
      pbrTile: 6,
      repeatM: 0.22,
      roughness: 0.66,
      sheen: 0.58,
      composition: '302cm Reversible Triple-Weave Acoustic Dimout',
      swatches: [
        { id: 'pearl', name: 'Pearl', hex: '#E8E4DC' },
        { id: 'cashmere', name: 'Cashmere', hex: '#C9BCA9' },
        { id: 'pewter', name: 'Pewter', hex: '#7A7D80' },
        { id: 'theatre-carbon', name: 'Theatre Carbon', hex: '#25272B' }
      ]
    },
    {
      id: 'zepel-wategos',
      supplier: 'Zepel Fabrics',
      name: 'Wategos Coastal Linen Drapery',
      category: 'drapery',
      rollWidthCm: 300,
      ratePerMetre: 75.00,
      velvetSurcharge: false,
      pbrTile: 4,
      repeatM: 0.32,
      roughness: 0.84,
      sheen: 0.48,
      composition: '300cm Heavyweight Belgian-Style Relaxed Linen Blend',
      swatches: [
        { id: 'shell', name: 'Sea Shell', hex: '#EFEBE2' },
        { id: 'sand', name: 'Byron Sand', hex: '#D7C8B2' },
        { id: 'driftwood', name: 'Driftwood', hex: '#B1A28E' },
        { id: 'olive', name: 'Coastal Olive', hex: '#6E735F' }
      ]
    },

    // Narrow By-Drop Fabrics (139cm - 145cm)
    {
      id: 'zepel-chief-cubo',
      supplier: 'Zepel Fabrics',
      name: 'Chief / Fellow / Cubo Woven',
      category: 'drapery',
      rollWidthCm: 145,
      ratePerMetre: 28.00,
      velvetSurcharge: false,
      pbrTile: 5, // slub-drapery (Twinbru 99978)
      repeatM: 0.26,
      roughness: 0.78,
      sheen: 0.50,
      composition: '145cm Tailored Drapery & Roman Blind Slub Weave',
      swatches: [
        { id: 'birch', name: 'Birch', hex: '#E9E4DA' },
        { id: 'wheat', name: 'Wheat', hex: '#CEBFA9' },
        { id: 'flint', name: 'Flint', hex: '#838588' },
        { id: 'navy', name: 'Classic Navy', hex: '#283344' }
      ]
    },
    {
      id: 'zepel-nightlife',
      supplier: 'Zepel Fabrics',
      name: 'Nightlife Textured Blockout',
      category: 'blockout',
      rollWidthCm: 139,
      ratePerMetre: 45.00,
      velvetSurcharge: false,
      pbrTile: 6,
      repeatM: 0.24,
      roughness: 0.72,
      sheen: 0.52,
      composition: '139cm 3-Pass Coated Designer Blockout',
      swatches: [
        { id: 'cream', name: 'Warm Cream', hex: '#EAE3D5' },
        { id: 'stone', name: 'Stone', hex: '#BDB2A1' },
        { id: 'charcoal', name: 'Charcoal', hex: '#414449' },
        { id: 'cinema-noir', name: 'Cinema Noir', hex: '#1B1C1E' }
      ]
    },
    {
      id: 'zepel-opera-velvet',
      supplier: 'Zepel Fabrics',
      name: 'Opera Acoustic Cinema Velvet',
      category: 'velvet',
      rollWidthCm: 140,
      ratePerMetre: 75.00,
      velvetSurcharge: true,
      pbrTile: 7, // cinema-velvet (Twinbru 80314)
      repeatM: 0.28,
      roughness: 0.44,
      sheen: 1.0,
      composition: '140cm Plush Acoustic Theatre & Lounge Velvet',
      swatches: [
        { id: 'onyx', name: 'Cinema Onyx Black', hex: '#18191B' },
        { id: 'crimson', name: 'Royale Crimson', hex: '#5D1920' },
        { id: 'sapphire', name: 'Midnight Sapphire', hex: '#1D293B' },
        { id: 'emerald', name: 'Forest Emerald', hex: '#1E3A2F' },
        { id: 'champagne', name: 'Champagne Gold', hex: '#BBA689' }
      ]
    },
    {
      id: 'mokum-bespoke-velvet',
      supplier: 'Mokum',
      name: 'CM by Mokum Bespoke Velvet',
      category: 'velvet',
      rollWidthCm: 140,
      ratePerMetre: 117.00,
      velvetSurcharge: true,
      pbrTile: 7,
      repeatM: 0.26,
      roughness: 0.40,
      sheen: 1.0,
      composition: '140cm Luxury Italian-Finish High-Pile Drapery Velvet',
      swatches: [
        { id: 'truffle', name: 'Truffle', hex: '#5A4E44' },
        { id: 'obsidian', name: 'Obsidian', hex: '#1A1B1D' },
        { id: 'moss', name: 'Deep Moss', hex: '#384434' },
        { id: 'alabaster', name: 'Warm Alabaster', hex: '#DDD5C7' }
      ]
    }
  ];

  const PLEAT_STYLES = [
    {
      id: 's-wave',
      name: 'S-Fold / S-Wave',
      badge: 'Most Popular',
      fullness: 2.0,
      isRoman: false,
      description: 'Architectural continuous sinusoidal wave suspended from precision S-Wave carrier cords.'
    },
    {
      id: 'double-pinch',
      name: 'Double Pinch Pleat',
      badge: 'Classic Tailored',
      fullness: 2.0,
      isRoman: false,
      description: 'Hand-sewn two-finger French pinch pleats for timeless volume and structured drape.'
    },
    {
      id: 'triple-pinch',
      name: 'Triple Pinch Pleat',
      badge: 'Luxury Fullness',
      fullness: 2.3,
      isRoman: false,
      description: 'Rich three-finger couture pleats with 230% fullness for formal living and home theatres.'
    },
    {
      id: 'single-pinch',
      name: 'Single Pinch Pleat',
      badge: 'Minimalist Tailored',
      fullness: 2.0,
      isRoman: false,
      description: 'Crisp single-tuck tailored header offering neat stacking in shallower pelmets.'
    },
    {
      id: 'knife-pleat',
      name: 'Knife Pleat',
      badge: 'Crisp Flat Fold',
      fullness: 2.0,
      isRoman: false,
      description: 'Directional flat knife folds for clean contemporary lines and slim stack depth.'
    },
    {
      id: 'inverted-pleat',
      name: 'Inverted Pleat',
      badge: 'Recessed Fold',
      fullness: 2.0,
      isRoman: false,
      description: 'Flat contemporary header face with pleats tucked neatly to the rear.'
    },
    {
      id: 'box-pleat',
      name: 'Box Pleat',
      badge: 'Structured Architectural',
      fullness: 2.0,
      isRoman: false,
      description: 'Symmetrical tailored box folds ideal for pelmets, theatre walls, and formal dining.'
    },
    {
      id: 'roman-blind',
      name: 'Custom Roman Blind',
      badge: 'Structured Fold',
      fullness: 1.0,
      isRoman: true,
      description: 'Rear-battened architectural horizontal folds on an Easy Lift 1:4 chain or cord headrail.'
    }
  ];

  const WINDOW_PRESETS = [
    { id: 'straight', name: 'Straight Window', defaultW: 2400, defaultD: 2400, defaultBends: 0 },
    { id: 'sliding-door', name: 'Sliding Door / Alfresco', defaultW: 3600, defaultD: 2700, defaultBends: 0 },
    {
      id: 'bay-window',
      name: '3-Sided Bay Window',
      defaultW: 3900,
      defaultD: 2500,
      defaultBends: 2,
      defaultBayLeft: 950,
      defaultBayCenter: 2000,
      defaultBayRight: 950
    },
    {
      id: 'corner-window',
      name: '90° Corner Window',
      defaultW: 3600,
      defaultD: 2600,
      defaultBends: 1,
      defaultCornerLeft: 1600,
      defaultCornerRight: 2000
    },
    { id: 'home-theatre', name: 'Home Theatre Acoustic Wall', defaultW: 4500, defaultD: 2700, defaultBends: 0 }
  ];

  function getFabricById(id) {
    return FABRIC_RANGES.find(f => f.id === id) || FABRIC_RANGES[0];
  }

  function getPleatById(id) {
    return PLEAT_STYLES.find(p => p.id === id) || PLEAT_STYLES[0];
  }

  /**
   * Lookup Track Workroom Price (exc. GST) from 21-bracket table.
   */
  function calculateTrackBase({
    trackSeriesId = 'styletrack',
    trackFinishId = 'white',
    mounting = 'top', // 'top' | 'face'
    layerMode = 'single', // 'single' | 'double'
    isWaveHeading = true,
    widthMm = 2400,
    bendsCount = 0
  }) {
    if (trackSeriesId === 'none') {
      return { baseRate: 0, description: 'No Track (Curtains Only)' };
    }
    const series = TRACK_TABLES[trackSeriesId] || TRACK_TABLES.styletrack;
    const finish = series.finishes.find(f => f.id === trackFinishId) || series.finishes[0];

    let tableKey = 'single_top_wave';
    if (layerMode === 'double') {
      tableKey = mounting === 'face' ? 'double_face_wave_glider' : 'double_top_wave_glider';
      if (!isWaveHeading && series.prices.double_top_glider) {
        tableKey = mounting === 'face' ? 'double_face_glider' : 'double_top_glider';
      }
    } else {
      if (isWaveHeading) {
        tableKey = mounting === 'face' ? 'single_face_wave' : 'single_top_wave';
      } else {
        tableKey = mounting === 'face' ? 'single_face_glider' : 'single_top_glider';
      }
    }
    const row = series.prices[tableKey] || series.prices.single_top_wave || series.prices.single_face_wave || Object.values(series.prices)[0];

    let baseTrackWorkroom = 0;
    const clampedW = Math.max(600, Number(widthMm) || 2400);

    if (clampedW <= 6000) {
      let idx = TRACK_WIDTH_BRACKETS.findIndex(b => b >= clampedW);
      if (idx === -1) idx = TRACK_WIDTH_BRACKETS.length - 1;
      baseTrackWorkroom = row[idx];
    } else if (trackSeriesId === 'saber_motorised') {
      const price6000 = row[row.length - 1];
      const extraMetres = Math.ceil((clampedW - 6000) / 1000);
      baseTrackWorkroom = price6000 + extraMetres * 50.66;
    } else {
      const halfW = Math.ceil(clampedW / 2);
      let idx = TRACK_WIDTH_BRACKETS.findIndex(b => b >= halfW);
      if (idx === -1) idx = TRACK_WIDTH_BRACKETS.length - 1;
      baseTrackWorkroom = row[idx] * 2;
    }

    const finishMultiplier = 1 + (finish.surchargePct || 0);
    const bendsCost = Math.max(0, Number(bendsCount) || 0) * (series.bendBaseRate || 30.0);
    const totalTrackWorkroom = +(baseTrackWorkroom * finishMultiplier + bendsCost).toFixed(2);

    return {
      baseRate: totalTrackWorkroom,
      seriesName: series.name,
      finishName: finish.name,
      finishHex: finish.hex,
      bendsCost,
      description: `${series.name} (${finish.name}, ${mounting === 'face' ? 'Wall Face Fix' : 'Ceiling Top Fix'}, ${layerMode === 'double' ? 'Double Track' : 'Single Track'}${bendsCount > 0 ? `, ${bendsCount}× 90° Bend` : ''})`
    };
  }

  /**
   * Calculate single curtain layer Workroom (2026 Workroom Price List + curtain calculations2.xlsx)
   * Supports 3 distinct lining tiers:
   * - 'unlined': $0 lining + Unlined make ($30/m continuous or $34/drop narrow)
   * - 'polycotton_sateen': $20/m wide / $12/m narrow + Lined make ($40/m continuous or $44/drop narrow)
   * - 'coated_3pass_blockout': $26/m wide / $16/m narrow + Lined make ($40/m continuous or $44/drop narrow)
   * Sheers are automatically manufactured unlined.
   */
  function calculateCurtainLayerBase({
    widthMm = 2400,
    dropMm = 2400,
    pleatId = 's-wave',
    fabricId = 'zepel-graceful',
    liningType = 'unlined' // 'unlined' | 'polycotton_sateen' | 'coated_3pass_blockout'
  }) {
    const fabric = getFabricById(fabricId);
    const pleat = getPleatById(pleatId);
    const fullness = pleat.fullness || 2.0;
    const effectiveLining = fabric.category === 'sheer' ? 'unlined' : (liningType || 'unlined');
    const isLined = effectiveLining !== 'unlined';
    const is3PassBlockout = effectiveLining === 'coated_3pass_blockout';

    const headerWidthCm = (widthMm / 10) * 1.10; // +10% header allowance per workroom spreadsheet
    const isContinuous = fabric.rollWidthCm >= 280 && (dropMm + 350 <= fabric.rollWidthCm * 10);

    let fabricMetres = 0;
    let dropsCount = 1;
    let unlinedMakingCost = 0;
    let makingCost = 0;
    let liningCost = 0;
    let methodLabel = '';

    if (isContinuous) {
      fabricMetres = Math.ceil((headerWidthCm * fullness + 30) / 100);
      const billedWidthMetres = Math.ceil(widthMm / 1000);
      const unlinedDropSurcharge = dropMm > 3500 ? 12.0 : dropMm > 3000 ? 8.0 : 0;
      const linedDropSurcharge = dropMm > 3500 ? 19.0 : dropMm > 3000 ? 12.0 : 0;

      unlinedMakingCost = billedWidthMetres * (30.0 + unlinedDropSurcharge);
      makingCost = isLined
        ? billedWidthMetres * (40.0 + linedDropSurcharge)
        : unlinedMakingCost;

      if (isLined) {
        const liningRatePerM = is3PassBlockout ? 26.0 : 20.0;
        liningCost = fabricMetres * liningRatePerM;
      }
      methodLabel = `Continuous (${fabric.rollWidthCm}cm wide roll, ${fabricMetres}m fabric)`;
    } else {
      const effectiveRollCm = fabric.rollWidthCm >= 280 ? 140 : fabric.rollWidthCm;
      dropsCount = Math.ceil((headerWidthCm * fullness) / effectiveRollCm);
      const cutDropMetres = ((dropMm / 10) + 35) / 100;
      fabricMetres = Math.ceil(dropsCount * cutDropMetres);

      let unlinedRatePerDrop = 34.0 + (dropMm > 3500 ? 12.0 : dropMm > 3000 ? 8.0 : 0) + (fabric.velvetSurcharge ? 8.0 : 0);
      let linedRatePerDrop = 44.0 + (dropMm > 3500 ? 19.0 : dropMm > 3000 ? 12.0 : 0) + (fabric.velvetSurcharge ? 12.0 : 0);

      unlinedMakingCost = dropsCount * unlinedRatePerDrop;
      makingCost = isLined ? dropsCount * linedRatePerDrop : unlinedMakingCost;

      if (isLined) {
        const liningRatePerM = is3PassBlockout ? 16.0 : 12.0;
        liningCost = fabricMetres * liningRatePerM;
      }
      methodLabel = `By Drop (${dropsCount} drops × ${fabric.rollWidthCm}cm roll, ${fabricMetres}m fabric)`;
    }

    const fabricCost = +(fabricMetres * fabric.ratePerMetre).toFixed(2);
    const baseUnlinedRate = +(fabricCost + unlinedMakingCost).toFixed(2);
    const liningUpgradeRate = +((makingCost - unlinedMakingCost) + liningCost).toFixed(2);
    const totalLayerRate = +(fabricCost + makingCost + liningCost).toFixed(2);

    const liningLabel = !isLined
      ? (fabric.category === 'sheer' ? 'Unlined (Sheer Daylight Filtering)' : 'Unlined (Natural Drape)')
      : is3PassBlockout
        ? '3-Pass Thermal & Acoustic Blockout Lining'
        : 'Polycotton Sateen Drapery Lining';

    return {
      isContinuous,
      dropsCount,
      fabricMetres,
      fabricCost,
      unlinedMakingCost,
      makingCost,
      liningCost,
      baseUnlinedRate,
      liningUpgradeRate,
      totalLayerRate,
      effectiveLining,
      liningLabel,
      methodLabel,
      fabricName: `${fabric.supplier} ${fabric.name}`,
      ratePerMetre: fabric.ratePerMetre
    };
  }

  /**
   * Calculate Roman Blind Workroom (2026 Workroom Roman Blinds Price List)
   */
  function calculateRomanBlindBase({
    widthMm = 1800,
    dropMm = 1800,
    fabricId = 'zepel-chief-cubo',
    driveType = 'chain-easy-lift' // 'cord' | 'cord-lock' | 'chain-easy-lift'
  }) {
    const fabric = getFabricById(fabricId);
    const clampedW = Math.min(3000, Math.max(400, Number(widthMm) || 1800));
    const clampedD = Math.min(3200, Math.max(600, Number(dropMm) || 1800));

    const iW = Math.max(0, Math.ceil((Math.max(clampedW, 1000) - 1000) / 250));
    const iD = Math.max(0, Math.ceil((Math.max(clampedD, 800) - 800) / 200));

    let baseGridRate = 0;
    let driveLabel = '';
    if (driveType === 'cord') {
      baseGridRate = 300 + 25 * iW + 25 * iD;
      driveLabel = 'Cord Operated Headrail (Incl. Blockout/Sateen Lining & Rear Battens)';
    } else if (driveType === 'cord-lock') {
      baseGridRate = 300 + 25 * iW + 25 * iD + 25;
      driveLabel = 'Cord Lock Pulley System (Incl. Blockout/Sateen Lining & Rear Battens)';
    } else {
      baseGridRate = 420 + 25 * iW + 25 * iD;
      driveLabel = 'Easy Lift 1:4 Heavy Duty Chain Drive (Incl. Lining & Rear Battens)';
    }

    const cutDropMetres = ((clampedD / 10) + 30) / 100;
    const panels = fabric.rollWidthCm >= 280 ? 1 : Math.max(1, Math.ceil((clampedW / 10 + 10) / fabric.rollWidthCm));
    const fabricMetres = Math.ceil(panels * cutDropMetres);
    const fabricCost = +(fabricMetres * fabric.ratePerMetre).toFixed(2);
    const totalRomanRate = +(baseGridRate + fabricCost).toFixed(2);

    return {
      baseGridRate,
      fabricMetres,
      fabricCost,
      totalRomanRate,
      driveLabel,
      fabricName: `${fabric.supplier} ${fabric.name}`
    };
  }

  /**
   * Master Pricing Engine
   * Returns complete Workroom & retail breakdown (1.75x Workroom + 10% GST = 1.925x Workroom)
   * Includes multi-blind Bay (3 blinds) & 90° Corner (2 blinds) support, tiered Lining breakdown, and Window Quantity.
   */
  function calculateCompletePricing(state) {
    const isBay = state.windowPreset === 'bay-window';
    const isCorner = state.windowPreset === 'corner-window';

    const bayLeftMm = Math.max(300, Math.min(4000, Number(state.bayLeftMm) || 950));
    const bayCenterMm = Math.max(400, Math.min(6000, Number(state.bayCenterMm) || 2000));
    const bayRightMm = Math.max(300, Math.min(4000, Number(state.bayRightMm) || 950));
    const cornerLeftMm = Math.max(300, Math.min(6000, Number(state.cornerLeftMm) || 1600));
    const cornerRightMm = Math.max(300, Math.min(6000, Number(state.cornerRightMm) || 2000));

    const computedWidthMm = isBay
      ? (bayLeftMm + bayCenterMm + bayRightMm)
      : isCorner
        ? (cornerLeftMm + cornerRightMm)
        : (Number(state.widthMm) || 2400);

    const widthMm = Math.max(600, Math.min(12000, computedWidthMm));
    const dropMm = Math.max(600, Math.min(4200, Number(state.dropMm) || 2400));
    const windowQty = Math.max(1, Math.min(20, Math.round(Number(state.windowQty) || 1)));
    const pleat = getPleatById(state.pleatId);

    if (pleat.isRoman) {
      const sectionWidths = isBay
        ? [bayLeftMm, bayCenterMm, bayRightMm]
        : isCorner
          ? [cornerLeftMm, cornerRightMm]
          : [widthMm];

      const blindCalcs = sectionWidths.map(w =>
        calculateRomanBlindBase({
          widthMm: w,
          dropMm,
          fabricId: state.primaryFabricId,
          driveType: state.romanDrive || 'chain-easy-lift'
        })
      );

      const totalBaseGrid = blindCalcs.reduce((acc, b) => acc + b.baseGridRate, 0);
      const totalFabMetres = blindCalcs.reduce((acc, b) => acc + b.fabricMetres, 0);
      const totalFabCost = +(blindCalcs.reduce((acc, b) => acc + b.fabricCost, 0)).toFixed(2);
      const totalRomanBase = +(blindCalcs.reduce((acc, b) => acc + b.totalRomanRate, 0)).toFixed(2);

      const sectionDesc = isBay
        ? `3× Roman Blinds (Left ${bayLeftMm}mm + Centre ${bayCenterMm}mm + Right ${bayRightMm}mm)`
        : isCorner
          ? `2× Roman Blinds (Left ${cornerLeftMm}mm + Right ${cornerRightMm}mm)`
          : `1× Roman Blind (${widthMm}mm)`;

      const roman = {
        baseGridRate: totalBaseGrid,
        fabricMetres: totalFabMetres,
        fabricCost: totalFabCost,
        totalRomanRate: totalRomanBase,
        driveLabel: `${blindCalcs[0].driveLabel} • ${sectionDesc}`,
        fabricName: blindCalcs[0].fabricName,
        blindsCount: sectionWidths.length,
        sectionDesc
      };

      const unitBaseSubtotal = +(roman.totalRomanRate + BOX_FREIGHT_BASE).toFixed(2);
      const unitRetailIncGst = Math.round(unitBaseSubtotal * STUDIO_RATE_FACTOR);
      const baseSubtotal = +(unitBaseSubtotal * windowQty).toFixed(2);
      const retailExGst = +(baseSubtotal * STUDIO_EX_GST_FACTOR).toFixed(2);
      const retailIncGst = unitRetailIncGst * windowQty;

      return {
        isRoman: true,
        widthMm,
        dropMm,
        windowQty,
        bayLeftMm,
        bayCenterMm,
        bayRightMm,
        cornerLeftMm,
        cornerRightMm,
        primaryLayer: roman,
        secondaryLayer: null,
        track: { baseRate: 0, description: roman.driveLabel },
        freightBase: BOX_FREIGHT_BASE * windowQty,
        baseTotal: baseSubtotal,
        retailExGst,
        unitRetailIncGst,
        retailIncGst,
        primaryRetailIncGst: Math.round(roman.totalRomanRate * STUDIO_RATE_FACTOR) * windowQty,
        liningRetailIncGst: 0,
        liningSummaryText: 'Included in Roman Blind Headrail & Make',
        secondaryRetailIncGst: 0,
        trackRetailIncGst: 0,
        freightRetailIncGst: Math.round(BOX_FREIGHT_BASE * STUDIO_RATE_FACTOR) * windowQty
      };
    }

    const primaryLayer = calculateCurtainLayerBase({
      widthMm,
      dropMm,
      pleatId: state.pleatId,
      fabricId: state.primaryFabricId,
      liningType: state.liningType || 'polycotton_sateen'
    });

    let secondaryLayer = null;
    if (state.layerMode === 'double') {
      secondaryLayer = calculateCurtainLayerBase({
        widthMm,
        dropMm,
        pleatId: state.secondaryPleatId || 's-wave',
        fabricId: state.secondaryFabricId || 'zepel-flo-ii',
        liningType: 'unlined'
      });
    }

    const effectiveBends = isBay
      ? Math.max(2, Number(state.bendsCount) || 2)
      : isCorner
        ? Math.max(1, Number(state.bendsCount) || 1)
        : (Number(state.bendsCount) || 0);

    const track = calculateTrackBase({
      trackSeriesId: state.trackSeriesId || 'styletrack',
      trackFinishId: state.trackFinishId || 'white',
      mounting: state.mounting || 'top',
      layerMode: state.layerMode || 'single',
      isWaveHeading: state.pleatId === 's-wave',
      widthMm,
      bendsCount: effectiveBends
    });

    const unitBaseTotal = +(
      primaryLayer.totalLayerRate +
      (secondaryLayer ? secondaryLayer.totalLayerRate : 0) +
      track.baseRate +
      BOX_FREIGHT_BASE
    ).toFixed(2);

    const unitRetailIncGst = Math.round(unitBaseTotal * STUDIO_RATE_FACTOR);
    const baseTotal = +(unitBaseTotal * windowQty).toFixed(2);
    const retailExGst = +(baseTotal * STUDIO_EX_GST_FACTOR).toFixed(2);
    const retailIncGst = unitRetailIncGst * windowQty;

    const basePrimaryRetail = Math.round(primaryLayer.baseUnlinedRate * STUDIO_RATE_FACTOR) * windowQty;
    const liningUpgradeRetail = Math.round(primaryLayer.liningUpgradeRate * STUDIO_RATE_FACTOR) * windowQty;

    const liningSummaryText = primaryLayer.effectiveLining === 'unlined'
      ? (getFabricById(state.primaryFabricId).category === 'sheer' ? '$0 (Unlined Sheer)' : '$0 (Unlined)')
      : `+$${liningUpgradeRetail.toLocaleString('en-AU')} (${primaryLayer.effectiveLining === 'coated_3pass_blockout' ? '3-Pass Blockout' : 'Polycotton Sateen'})`;

    return {
      isRoman: false,
      widthMm,
      dropMm,
      windowQty,
      bayLeftMm,
      bayCenterMm,
      bayRightMm,
      cornerLeftMm,
      cornerRightMm,
      primaryLayer,
      secondaryLayer,
      track,
      freightBase: BOX_FREIGHT_BASE * windowQty,
      baseTotal,
      retailExGst,
      unitRetailIncGst,
      retailIncGst,
      primaryRetailIncGst: basePrimaryRetail,
      liningRetailIncGst: liningUpgradeRetail,
      liningSummaryText,
      secondaryRetailIncGst: secondaryLayer ? Math.round(secondaryLayer.totalLayerRate * STUDIO_RATE_FACTOR) * windowQty : 0,
      trackRetailIncGst: Math.round(track.baseRate * STUDIO_RATE_FACTOR) * windowQty,
      freightRetailIncGst: Math.round(BOX_FREIGHT_BASE * STUDIO_RATE_FACTOR) * windowQty
    };
  }

  // Colour utilities
  function hexToRgb(hex) {
    const clean = String(hex || '#E5DFD3').replace('#', '');
    const num = parseInt(clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean, 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255
    };
  }

  function rgbaStr(rgb, alpha, brightness = 1) {
    const r = Math.min(255, Math.max(0, Math.round(rgb.r * brightness)));
    const g = Math.min(255, Math.max(0, Math.round(rgb.g * brightness)));
    const b = Math.min(255, Math.max(0, Math.round(rgb.b * brightness)));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // ============================================================================
  // THREE.JS (r170) WEBGL PBR 3D ENGINE + TWINBRU TEXTURE ATLAS + REAL ROOM COMPOSITOR
  // ============================================================================

  const ASSET_CACHE = {
    started: false,
    pbrAtlases: {}, // d, n, r, a, s Image elements
    pbrTiles: {},   // key: `${type}_${tileIndex}` -> THREE.CanvasTexture
    roomPhotos: {}, // living, bay, theatre, outdoor, floorOak Image elements
    threeTex: {},   // outdoor, floorOak THREE.Texture
    listeners: []
  };

  function resolveAssetUrls(rootEl) {
    const ds = rootEl?.dataset || {};
    return {
      pbrD: ds.pbrD || 'shopify/assets/ll-curtain-pbr-d.webp',
      pbrN: ds.pbrN || 'shopify/assets/ll-curtain-pbr-n.webp',
      pbrR: ds.pbrR || 'shopify/assets/ll-curtain-pbr-r.webp',
      pbrA: ds.pbrA || 'shopify/assets/ll-curtain-pbr-a.webp',
      pbrS: ds.pbrS || 'shopify/assets/ll-curtain-pbr-s.webp',
      roomLiving: ds.roomLiving || 'shopify/assets/ll-curtain-room-living.webp',
      roomBay: ds.roomBay || 'shopify/assets/ll-curtain-room-bay.webp',
      roomTheatre: ds.roomTheatre || 'shopify/assets/ll-curtain-room-theatre.webp',
      outdoorView: ds.outdoorView || 'shopify/assets/ll-curtain-outdoor-view.webp',
      floorOak: ds.floorOak || 'shopify/assets/ll-curtain-floor-oak.webp'
    };
  }

  function ensureAssetsLoaded(rootEl, onUpdate) {
    if (onUpdate && !ASSET_CACHE.listeners.includes(onUpdate)) {
      ASSET_CACHE.listeners.push(onUpdate);
    }
    if (ASSET_CACHE.started || typeof Image === 'undefined') return;
    ASSET_CACHE.started = true;

    const urls = resolveAssetUrls(rootEl);
    const notify = () => {
      ASSET_CACHE.listeners.forEach(fn => {
        try { fn(); } catch {}
      });
    };

    const loadImg = (url, onLoad) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        onLoad(img);
        notify();
      };
      img.src = url;
    };

    loadImg(urls.pbrD, img => { ASSET_CACHE.pbrAtlases.d = img; ASSET_CACHE.pbrTiles = {}; });
    loadImg(urls.pbrN, img => { ASSET_CACHE.pbrAtlases.n = img; ASSET_CACHE.pbrTiles = {}; });
    loadImg(urls.pbrR, img => { ASSET_CACHE.pbrAtlases.r = img; ASSET_CACHE.pbrTiles = {}; });
    loadImg(urls.pbrA, img => { ASSET_CACHE.pbrAtlases.a = img; ASSET_CACHE.pbrTiles = {}; });
    loadImg(urls.pbrS, img => { ASSET_CACHE.pbrAtlases.s = img; ASSET_CACHE.pbrTiles = {}; });

    loadImg(urls.roomLiving, img => { ASSET_CACHE.roomPhotos.living = img; });
    loadImg(urls.roomBay, img => { ASSET_CACHE.roomPhotos.bay = img; });
    loadImg(urls.roomTheatre, img => { ASSET_CACHE.roomPhotos.theatre = img; });
    loadImg(urls.outdoorView, img => { ASSET_CACHE.roomPhotos.outdoor = img; });
    loadImg(urls.floorOak, img => { ASSET_CACHE.roomPhotos.floorOak = img; });
  }

  /**
   * Extract a 512x512 seamless PBR tile from the 1536x1536 (3x3) Twinbru WebP atlas.
   * For diffuse ('d'), normalizes average luminance to white so swatch tinting preserves weave yarn contrast.
   */
  function getPbrTileTexture(THREE, mapType, tileIndex, repeatX, repeatY) {
    const atlasImg = ASSET_CACHE.pbrAtlases[mapType];
    if (!atlasImg || typeof document === 'undefined') return null;

    const idx = Math.max(0, Math.min(8, Number(tileIndex) || 0));
    const baseKey = `${mapType}_${idx}`;

    if (!ASSET_CACHE.pbrTiles[baseKey]) {
      const tileW = 512;
      const tileH = 512;
      const col = idx % 3;
      const row = Math.floor(idx / 3);

      const c = document.createElement('canvas');
      c.width = tileW;
      c.height = tileH;
      const ctx = c.getContext('2d');
      ctx.drawImage(atlasImg, col * tileW, row * tileH, tileW, tileH, 0, 0, tileW, tileH);

      if (mapType === 'd') {
        // Normalize diffuse tile into a high-detail neutral luminance weave map (0.74 .. 1.12)
        // so that multiplying by any swatch hex keeps crisp Twinbru yarn micro-contrast
        try {
          const imgData = ctx.getImageData(0, 0, tileW, tileH);
          const data = imgData.data;
          let sum = 0;
          const count = tileW * tileH;
          for (let i = 0; i < data.length; i += 4) {
            sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          }
          const avg = Math.max(24, sum / count);
          for (let i = 0; i < data.length; i += 4) {
            const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            const norm = 232 + (lum - avg) * 1.65;
            const v = Math.max(155, Math.min(255, Math.round(norm)));
            data[i] = v;
            data[i + 1] = v;
            data[i + 2] = v;
          }
          ctx.putImageData(imgData, 0, 0);
        } catch {}
      } else if (mapType === 'a') {
        // Enhance yarn open-weave alpha contrast for realistic sheer transmission
        try {
          const imgData = ctx.getImageData(0, 0, tileW, tileH);
          const data = imgData.data;
          for (let i = 0; i < data.length; i += 4) {
            const lum = data[i];
            const boosted = Math.max(95, Math.min(255, Math.round(135 + (lum - 128) * 1.25)));
            data[i] = boosted;
            data[i + 1] = boosted;
            data[i + 2] = boosted;
          }
          ctx.putImageData(imgData, 0, 0);
        } catch {}
      }

      ASSET_CACHE.pbrTiles[baseKey] = c;
    }

    const tex = new THREE.CanvasTexture(ASSET_CACHE.pbrTiles[baseKey]);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(repeatX, repeatY);
    tex.anisotropy = 8;
    if (mapType === 'd') {
      tex.colorSpace = THREE.SRGBColorSpace;
    } else {
      tex.colorSpace = THREE.NoColorSpace;
    }
    tex.needsUpdate = true;
    return tex;
  }

  /**
   * Build high-resolution 3D sculpted drapery panel BufferGeometry.
   * Includes:
   * - Authentic header profile at top (y = H) transitioning smoothly into deep gravity folds at bottom (y = 0)
   * - Subtle organic fold variation + secondary cloth ripple harmonics so no two pleats look mechanically identical
   * - 100mm double-turned bottom hem ridge & weight crease near y = 0..0.10m
   */
  function buildCurtainPanelGeometry(THREE, {
    panelWidthM,
    dropM,
    pleatId,
    numFolds,
    stackFactor, // 0 = fully drawn, 1 = stacked back
    isMacro,
    sideSign     // -1 left panel, +1 right panel
  }) {
    const segsX = Math.max(96, Math.min(240, numFolds * (isMacro ? 20 : 14)));
    const segsY = isMacro ? 64 : 48;
    const geom = new THREE.PlaneGeometry(panelWidthM, dropM, segsX, segsY);
    const pos = geom.attributes.position;
    const uv = geom.attributes.uv;

    // Fold depth increases naturally when curtain is stacked open
    const baseDepth = pleatId === 's-wave'
      ? 0.068 + stackFactor * 0.032
      : pleatId === 'triple-pinch'
        ? 0.064 + stackFactor * 0.028
        : pleatId === 'double-pinch'
          ? 0.056 + stackFactor * 0.026
          : 0.048 + stackFactor * 0.022;

    const headerHeightM = Math.min(0.14, dropM * 0.055);
    const hemHeightM = Math.min(0.105, dropM * 0.042);

    for (let i = 0; i < pos.count; i++) {
      const origX = pos.getX(i);
      const origY = pos.getY(i);
      const u = Math.max(0, Math.min(1, (origX / panelWidthM) + 0.5)); // 0..1 across panel width
      const vFromTop = Math.max(0, Math.min(1, 0.5 - (origY / dropM))); // 0 at top header, 1 at bottom hem

      const foldCoord = u * numFolds;
      const foldIndex = Math.floor(foldCoord);
      const localPhase = foldCoord - foldIndex; // 0..1 within single pleat
      const theta = foldCoord * Math.PI * 2;

      // Subtle deterministic organic variation per pleat fold
      const organicAmp = 1.0 + 0.075 * Math.sin(foldIndex * 2.17 + sideSign * 0.8) + 0.04 * Math.cos(foldIndex * 3.71);
      const gravitySpread = 1.0 + 0.24 * Math.pow(vFromTop, 1.35);

      let zHeader = 0;
      let zBody = 0;
      let xPinchOffset = 0;

      if (pleatId === 's-wave') {
        // True sinusoidal S-Wave carrier cord path at top & full sinusoidal drape body
        const sWave = Math.sin(theta);
        const harmonic = -0.14 * Math.sin(theta * 3); // flatten apex slightly like real S-fold tape
        zHeader = (sWave + harmonic) * baseDepth * 0.92;
        zBody = (sWave + 0.08 * harmonic) * baseDepth * gravitySpread * organicAmp;
        xPinchOffset = Math.cos(theta) * 0.008 * vFromTop;
      } else if (pleatId === 'double-pinch' || pleatId === 'triple-pinch' || pleatId === 'single-pinch') {
        // Stitched French pinch pleat: pinched tightly at buckram tack point (vFromTop ~ 0.035),
        // fanning out into cylindrical flutes below
        const fingers = pleatId === 'triple-pinch' ? 3 : pleatId === 'double-pinch' ? 2 : 1;
        const centered = localPhase - 0.5; // -0.5 .. 0.5
        const pinchZone = Math.abs(centered) < 0.23;

        if (pinchZone) {
          const normP = centered / 0.23; // -1 .. 1
          const fingerWave = Math.cos(normP * Math.PI * fingers);
          zHeader = 0.022 + (fingerWave * 0.5 + 0.5) * baseDepth * 0.72;
          xPinchOffset = -centered * (panelWidthM / numFolds) * 0.36 * (1 - Math.min(1, vFromTop * 4.5));
        } else {
          // Flat buckram interval between pleats curving back slightly
          const backT = (Math.abs(centered) - 0.23) / 0.27;
          zHeader = -Math.sin(backT * Math.PI * 0.5) * baseDepth * 0.55;
        }

        const bodyFlute = Math.cos((localPhase - 0.5) * Math.PI * 2);
        const fluteShape = bodyFlute + 0.22 * Math.cos((localPhase - 0.5) * Math.PI * 4);
        zBody = fluteShape * baseDepth * 0.82 * gravitySpread * organicAmp;
      } else if (pleatId === 'box-pleat' || pleatId === 'inverted-pleat') {
        // Architectural tailored box / inverted pleat
        const dFromCenter = Math.abs(localPhase - 0.5);
        const flatFace = pleatId === 'box-pleat'
          ? (dFromCenter < 0.30 ? 1.0 : -0.85)
          : (dFromCenter > 0.16 ? 0.85 : -1.0);
        const smoothed = Math.tanh(flatFace * 3.2) * 0.55 + 0.45 * Math.cos((localPhase - 0.5) * Math.PI * 2);
        zHeader = flatFace * baseDepth * 0.55;
        zBody = smoothed * baseDepth * 0.85 * gravitySpread * organicAmp;
      } else {
        // Knife pleat directional sawtooth wave
        const saw = (localPhase - 0.5) * 1.5;
        const smoothSaw = 0.65 * Math.sin((localPhase - 0.5) * Math.PI * 2) + 0.35 * saw;
        zHeader = saw * baseDepth * 0.52;
        zBody = smoothSaw * baseDepth * 0.82 * gravitySpread * organicAmp;
      }

      // Smooth transition from stitched header (top 12cm) into hanging body folds
      const headerBlend = Math.min(1, Math.pow(Math.max(0, vFromTop / (headerHeightM / dropM)), 1.25));
      let z = zHeader * (1 - headerBlend) + zBody * headerBlend;

      // Secondary natural cloth micro-drape ripple
      const microRipple = Math.sin(u * 29.0 + vFromTop * 5.2 + sideSign) * 0.0028 * Math.pow(vFromTop, 0.8);
      z += microRipple;

      // 100mm double-turned weighted bottom hem subtle seam indentation & slight outward kick
      const distFromBottomM = (1 - vFromTop) * dropM;
      if (distFromBottomM < hemHeightM) {
        const hemRatio = distFromBottomM / hemHeightM; // 0 at bottom edge, 1 at stitch line
        const stitchIndent = Math.exp(-Math.pow((hemRatio - 0.92) * 14, 2)) * -0.0032;
        z += stitchIndent + (1 - hemRatio) * 0.0035;
      }

      // Natural bottom flare outward at leading/trailing edges
      const flareX = (u - 0.5) * 0.018 * Math.pow(vFromTop, 1.6);

      // Slight vertical rise at forward fold crests (real fabric path length conservation)
      const crestLiftY = Math.sin(theta) * 0.0035 * vFromTop;

      pos.setXYZ(i, origX + xPinchOffset + flareX, origY + crestLiftY, z);

      // Physical arc-length UV mapping so weave repeats accurately around 3D folds
      const physicalU = u * (1.55 + stackFactor * 0.35);
      uv.setXY(i, physicalU, 1 - vFromTop);
    }

    geom.computeVertexNormals();
    return geom;
  }

  /**
   * Build 3D stitched pinch-pleat fingers & buckram tack bars along top of curtain panel
   */
  function buildPinchHeaderDetails(THREE, {
    panelWidthM,
    dropM,
    pleatId,
    numFolds,
    stackFactor,
    material
  }) {
    const group = new THREE.Group();
    if (!['single-pinch', 'double-pinch', 'triple-pinch'].includes(pleatId)) {
      return group;
    }

    const fingers = pleatId === 'triple-pinch' ? 3 : pleatId === 'double-pinch' ? 2 : 1;
    const baseDepth = pleatId === 'triple-pinch'
      ? 0.064 + stackFactor * 0.028
      : pleatId === 'double-pinch'
        ? 0.056 + stackFactor * 0.026
        : 0.048 + stackFactor * 0.022;

    const fingerHeight = Math.min(0.115, dropM * 0.048);
    const fingerRadius = fingers === 3 ? 0.0075 : fingers === 2 ? 0.009 : 0.0105;
    const fingerGeom = new THREE.CylinderGeometry(fingerRadius * 0.92, fingerRadius * 0.58, fingerHeight, 12);
    const tackGeom = new THREE.BoxGeometry(fingerRadius * (fingers * 2.2 + 0.6), 0.006, fingerRadius * 1.7);

    for (let fIdx = 0; fIdx < numFolds; fIdx++) {
      const uMid = (fIdx + 0.5) / numFolds;
      const xCenter = (uMid - 0.5) * panelWidthM;
      const yCenter = dropM * 0.5 - fingerHeight * 0.52;
      const zFront = baseDepth * 0.78;

      for (let k = 0; k < fingers; k++) {
        const offset = (k - (fingers - 1) * 0.5) * (fingerRadius * 2.05);
        const fingerMesh = new THREE.Mesh(fingerGeom, material);
        fingerMesh.position.set(xCenter + offset, yCenter, zFront);
        fingerMesh.rotation.z = -offset * 1.4;
        fingerMesh.castShadow = true;
        fingerMesh.receiveShadow = true;
        group.add(fingerMesh);
      }

      const tackMesh = new THREE.Mesh(tackGeom, material);
      tackMesh.position.set(xCenter, yCenter - fingerHeight * 0.36, zFront + fingerRadius * 0.35);
      tackMesh.castShadow = true;
      group.add(tackMesh);
    }

    return group;
  }

  /**
   * Build 3D Roman Blind Assembly with horizontal rear-batten pockets, cascading bottom folds,
   * Easy Lift 1:4 headrail, and stainless ball-chain loop.
   */
  function buildRomanBlindAssembly(THREE, {
    widthM,
    dropM,
    drawPct,
    fabricMaterial,
    driveType
  }) {
    const group = new THREE.Group();
    const activeDropM = Math.max(dropM * 0.22, dropM * drawPct);
    const topY = dropM * 0.5;
    const numBattens = 6;
    const battenHeight = activeDropM / numBattens;

    // Main Roman blind fabric plane with subtle pillowed horizontal batten folds
    const segsX = 48;
    const segsY = 72;
    const planeGeom = new THREE.PlaneGeometry(widthM, activeDropM, segsX, segsY);
    const pos = planeGeom.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const v = 0.5 - (y / activeDropM); // 0 top .. 1 bottom
      const battenPhase = (v * numBattens) % 1;
      // Subtle soft forward drape between rear battens + sharp tuck at each batten pocket
      const pillowZ = Math.sin(battenPhase * Math.PI) * 0.014 - Math.exp(-Math.pow((battenPhase - 0.98) * 18, 2)) * 0.006;
      const edgeCurl = Math.cos((x / widthM) * Math.PI) * 0.004;
      pos.setZ(i, pillowZ + edgeCurl);
    }
    planeGeom.computeVertexNormals();

    const mainMesh = new THREE.Mesh(planeGeom, fabricMaterial);
    mainMesh.position.set(0, topY - activeDropM * 0.5, 0.03);
    mainMesh.castShadow = true;
    mainMesh.receiveShadow = true;
    group.add(mainMesh);

    // Horizontal stitched batten pocket bars
    const battenRodGeom = new THREE.CylinderGeometry(0.0035, 0.0035, widthM * 0.992, 10);
    battenRodGeom.rotateZ(Math.PI * 0.5);
    for (let b = 1; b < numBattens; b++) {
      const by = topY - b * battenHeight;
      const pocketMesh = new THREE.Mesh(battenRodGeom, fabricMaterial);
      pocketMesh.position.set(0, by, 0.026);
      pocketMesh.castShadow = true;
      group.add(pocketMesh);
    }

    // Cascading soft roman folds stacked at the bottom when blind is raised
    const stackCount = Math.max(2, Math.round((1.08 - drawPct) * 6));
    const bottomY = topY - activeDropM;
    for (let s = 0; s < stackCount; s++) {
      const loopDrop = 0.065 + s * 0.008;
      const loopGeom = new THREE.CylinderGeometry(0.022, 0.026, widthM * 0.996, 20, 1, false, 0, Math.PI);
      loopGeom.rotateZ(Math.PI * 0.5);
      loopGeom.scale(1, 1.35, 0.72);
      const loopMesh = new THREE.Mesh(loopGeom, fabricMaterial);
      loopMesh.position.set(0, bottomY + 0.028 - s * (loopDrop * 0.42), 0.038 + s * 0.009);
      loopMesh.castShadow = true;
      loopMesh.receiveShadow = true;
      group.add(loopMesh);
    }

    // Easy Lift 1:4 Aluminium Headrail at top
    const headrailMat = new THREE.MeshStandardMaterial({
      color: 0xF3F2EE,
      metalness: 0.25,
      roughness: 0.34
    });
    const headrailMesh = new THREE.Mesh(
      new THREE.BoxGeometry(widthM * 1.005, 0.042, 0.048),
      headrailMat
    );
    headrailMesh.position.set(0, topY + 0.012, 0.025);
    headrailMesh.castShadow = true;
    group.add(headrailMesh);

    // Stainless steel chain or cord loop on right edge
    const chainMat = new THREE.MeshStandardMaterial({
      color: driveType === 'chain-easy-lift' ? 0xD4D8DD : 0xEAE4D7,
      metalness: driveType === 'chain-easy-lift' ? 0.88 : 0.1,
      roughness: driveType === 'chain-easy-lift' ? 0.22 : 0.75
    });
    const chainDrop = Math.min(dropM * 0.68, 1.35);
    const chainCylinder = new THREE.CylinderGeometry(0.0024, 0.0024, chainDrop, 8);
    const chainL = new THREE.Mesh(chainCylinder, chainMat);
    const chainR = new THREE.Mesh(chainCylinder, chainMat);
    chainL.position.set(widthM * 0.485, topY - chainDrop * 0.5, 0.048);
    chainR.position.set(widthM * 0.493, topY - chainDrop * 0.5, 0.048);
    group.add(chainL, chainR);

    return group;
  }

  /**
   * Build 3D Extruded Track / Decorator Rod Hardware (Styletrack, Snap Fit, Commercial, Designer 25 Rod, Saber Motorised)
   */
  function buildTrackHardwareGroup(THREE, {
    widthM,
    dropM,
    trackSeriesId,
    trackFinishId,
    layerMode,
    mounting,
    bendsCount
  }) {
    const group = new THREE.Group();
    if (trackSeriesId === 'none') return group;

    const series = TRACK_TABLES[trackSeriesId] || TRACK_TABLES.styletrack;
    const finish = series.finishes.find(f => f.id === trackFinishId) || series.finishes[0];

    const trackMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(finish.hex),
      metalness: finish.metalness ?? 0.35,
      roughness: finish.roughness ?? 0.35
    });

    const topY = dropM * 0.5 + 0.016;
    const spanW = widthM * 1.03;
    const isRod = trackSeriesId === 'designer25';
    const isMotorised = trackSeriesId === 'saber_motorised';

    const buildSingleRail = (zOffset, scaleR = 1) => {
      if (isRod) {
        const rodGeom = new THREE.CylinderGeometry(0.0135 * scaleR, 0.0135 * scaleR, spanW, 24);
        rodGeom.rotateZ(Math.PI * 0.5);
        const rodMesh = new THREE.Mesh(rodGeom, trackMat);
        rodMesh.position.set(0, topY, zOffset);
        rodMesh.castShadow = true;
        group.add(rodMesh);

        // Architectural cylinder end-cap finials
        const finialGeom = new THREE.CylinderGeometry(0.0175 * scaleR, 0.0175 * scaleR, 0.045, 20);
        finialGeom.rotateZ(Math.PI * 0.5);
        [-spanW * 0.5 - 0.018, spanW * 0.5 + 0.018].forEach(fx => {
          const fMesh = new THREE.Mesh(finialGeom, trackMat);
          fMesh.position.set(fx, topY, zOffset);
          fMesh.castShadow = true;
          group.add(fMesh);
        });
      } else {
        const profileH = isMotorised ? 0.032 : trackSeriesId === 'snapfit' ? 0.019 : 0.025;
        const profileD = isMotorised ? 0.030 : 0.022;
        const railGeom = new THREE.BoxGeometry(spanW, profileH, profileD);
        const railMesh = new THREE.Mesh(railGeom, trackMat);
        railMesh.position.set(0, topY, zOffset);
        railMesh.castShadow = true;
        railMesh.receiveShadow = true;
        group.add(railMesh);

        // Bottom glider channel slot shadow line
        const slotMat = new THREE.MeshBasicMaterial({ color: 0x141517 });
        const slotMesh = new THREE.Mesh(new THREE.BoxGeometry(spanW * 0.995, 0.003, 0.006), slotMat);
        slotMesh.position.set(0, topY - profileH * 0.5, zOffset);
        group.add(slotMesh);
      }
    };

    // Primary front track at z = 0.095
    buildSingleRail(0.095, 1.0);

    // Secondary rear track at z = -0.015 if Double Track is selected
    if (layerMode === 'double') {
      buildSingleRail(-0.015, 0.88);
    }

    // Wall Face-Fix or Ceiling Top-Fix mounting brackets
    const numBrackets = Math.max(3, Math.min(7, Math.round(spanW / 0.85) + 1));
    for (let b = 0; b < numBrackets; b++) {
      const bx = -spanW * 0.46 + (spanW * 0.92 * b) / (numBrackets - 1);
      if (mounting === 'face') {
        const armDepth = layerMode === 'double' ? 0.16 : 0.11;
        const bracketMesh = new THREE.Mesh(
          new THREE.BoxGeometry(0.014, 0.022, armDepth),
          trackMat
        );
        bracketMesh.position.set(bx, topY + 0.008, 0.095 - armDepth * 0.45);
        bracketMesh.castShadow = true;
        group.add(bracketMesh);
      } else {
        const clipMesh = new THREE.Mesh(
          new THREE.BoxGeometry(0.024, 0.018, layerMode === 'double' ? 0.15 : 0.04),
          trackMat
        );
        clipMesh.position.set(bx, topY + 0.018, layerMode === 'double' ? 0.04 : 0.095);
        group.add(clipMesh);
      }
    }

    // Saber Motorised drive unit tucked behind right end of track
    if (isMotorised) {
      const motorMat = new THREE.MeshStandardMaterial({
        color: 0x242629,
        metalness: 0.4,
        roughness: 0.35
      });
      const motorMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.024, 0.24, 16), motorMat);
      motorMesh.position.set(spanW * 0.46, topY - 0.13, 0.06);
      group.add(motorMesh);
    }

    // Custom 90° / 135° Bay / Corner bend joint collars
    if (bendsCount > 0) {
      const collarMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(finish.hex),
        metalness: 0.75,
        roughness: 0.22
      });
      const positions = bendsCount === 1 ? [-spanW * 0.15] : [-spanW * 0.26, spanW * 0.26];
      positions.forEach(px => {
        const collar = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.032, 0.032), collarMat);
        collar.position.set(px, topY, 0.095);
        group.add(collar);
      });
    }

    return group;
  }

  /**
   * Create or retrieve the per-canvas Three.js WebGL PBR Context
   */
  function getOrInitWebGLContext(canvas, rootEl) {
    if (typeof window === 'undefined' || !window.THREE) return null;
    if (canvas.__llWebGLCtx) return canvas.__llWebGLCtx;

    const THREE = window.THREE;
    const W = 1280;
    const H = 720;

    const offscreen = document.createElement('canvas');
    offscreen.width = W;
    offscreen.height = H;

    let renderer = null;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas: offscreen,
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance'
      });
    } catch {
      return null;
    }

    renderer.setSize(W, H, false);
    renderer.setPixelRatio(1);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();

    // Build Studio HDRI Environment via RoomEnvironment PMREMGenerator
    if (THREE.RoomEnvironment) {
      try {
        const pmremGenerator = new THREE.PMREMGenerator(renderer);
        const envScene = new THREE.RoomEnvironment();
        const envRT = pmremGenerator.fromScene(envScene, 0.04);
        scene.environment = envRT.texture;
        scene.environmentIntensity = 0.72;
      } catch {}
    }

    const camera = new THREE.PerspectiveCamera(34, W / H, 0.05, 30);
    camera.position.set(0, 0.05, 4.35);

    let controls = null;
    if (THREE.OrbitControls) {
      controls = new THREE.OrbitControls(camera, canvas);
      controls.enableDamping = false;
      controls.enablePan = true;
      controls.minDistance = 0.55;
      controls.maxDistance = 7.5;
      controls.maxPolarAngle = Math.PI * 0.53;
      controls.minPolarAngle = Math.PI * 0.22;
      controls.minAzimuthAngle = -Math.PI * 0.36;
      controls.maxAzimuthAngle = Math.PI * 0.36;
      controls.target.set(0, 0.02, 0.05);
      controls.enabled = false; // enabled in 'angle', 'header', 'weave' 3D Inspector views
    }

    // Studio Lighting Rig
    const hemiLight = new THREE.HemisphereLight(0xFFFBF2, 0x9C8B75, 0.95);
    hemiLight.position.set(0, 3.5, 1.5);
    scene.add(hemiLight);

    // Key Sunlight / Architectural Directional Light with Soft Shadows
    const keyLight = new THREE.DirectionalLight(0xFFF7E8, 1.85);
    keyLight.position.set(1.65, 2.35, 3.1);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 2048;
    keyLight.shadow.mapSize.height = 2048;
    keyLight.shadow.camera.near = 0.5;
    keyLight.shadow.camera.far = 10;
    keyLight.shadow.camera.left = -3.2;
    keyLight.shadow.camera.right = 3.2;
    keyLight.shadow.camera.top = 2.4;
    keyLight.shadow.camera.bottom = -2.4;
    keyLight.shadow.bias = -0.0005;
    keyLight.shadow.radius = 3.5;
    scene.add(keyLight);

    // Window Daylight Rim / Backlight (drives sheer translucency & fold rim highlights)
    const windowBackLight = new THREE.DirectionalLight(0xEAF4FF, 1.45);
    windowBackLight.position.set(0, 0.6, -2.5);
    scene.add(windowBackLight);

    // Warm Interior Fill Light
    const fillLight = new THREE.DirectionalLight(0xFFEBD2, 0.68);
    fillLight.position.set(-2.2, 0.8, 2.4);
    scene.add(fillLight);

    const rootGroup = new THREE.Group();
    scene.add(rootGroup);

    const roomEnvGroup = new THREE.Group();
    scene.add(roomEnvGroup);

    const ctxObj = {
      THREE,
      offscreen,
      renderer,
      scene,
      camera,
      controls,
      hemiLight,
      keyLight,
      windowBackLight,
      fillLight,
      rootGroup,
      roomEnvGroup,
      lastPresetCamera: null
    };

    canvas.__llWebGLCtx = ctxObj;
    ensureAssetsLoaded(rootEl, () => {
      if (canvas.__llLastRenderArgs) {
        const { state, pricing, options } = canvas.__llLastRenderArgs;
        renderStudioCanvas(canvas, state, pricing, options);
      }
    });

    return ctxObj;
  }

  /**
   * Compute 3D Window & Track Segments for Straight, 3-Sided Bay (135° bay wings), and 90° Internal Corner Windows.
   * All coordinates are in Three.js meters centered around y=0 (window vertical center).
   */
  function compute3DWindowLayout(state, pricing, widthM, dropM, renderMode) {
    if (renderMode === 'overlay') {
      return {
        preset: 'overlay',
        segments: [
          {
            id: 'main',
            widthM: 2.65,
            x: 0,
            z: 0,
            rotY: 0,
            stackMode: state.stacking || 'centre-open',
            trimLeftM: 0,
            trimRightM: 0
          }
        ],
        bends: []
      };
    }

    const scaleMPerMm = dropM / Math.max(1550, pricing.dropMm);

    if (state.windowPreset === 'bay-window') {
      // 3-Sided Bay Window: Left Wing (+45° yaw), Centre Bay (0° yaw), Right Wing (-45° yaw)
      const bayAngle = Math.PI / 4; // 45° splay => 135° internal bay angle
      const wCenter = Math.max(1.05, Math.min(2.15, pricing.bayCenterMm * scaleMPerMm));
      const wLeft = Math.max(0.68, Math.min(1.30, pricing.bayLeftMm * scaleMPerMm));
      const wRight = Math.max(0.68, Math.min(1.30, pricing.bayRightMm * scaleMPerMm));

      const zCenter = -0.46;
      const vL = { x: -wCenter * 0.5, z: zCenter };
      const vR = { x: wCenter * 0.5, z: zCenter };

      const cosA = Math.cos(bayAngle);
      const sinA = Math.sin(bayAngle);

      const vLOuter = { x: vL.x - wLeft * cosA, z: vL.z + wLeft * sinA };
      const vROuter = { x: vR.x + wRight * cosA, z: vR.z + wRight * sinA };

      // Mitre trim factor tan(45° / 2) = 0.4142 so offset curtain/track planes meet cleanly at the 135° corner
      const mitreFactor = Math.tan(bayAngle * 0.5);

      return {
        preset: 'bay-window',
        bayAngle,
        vLOuter,
        vL,
        vR,
        vROuter,
        segments: [
          {
            id: 'bay-left',
            label: `Left Wing (${pricing.bayLeftMm}mm)`,
            widthM: wLeft,
            x: (vLOuter.x + vL.x) * 0.5,
            z: (vLOuter.z + vL.z) * 0.5,
            rotY: bayAngle,
            stackMode: state.stacking === 'right-stack' ? 'right-stack' : 'left-stack',
            trimLeftM: 0,
            trimRightM: mitreFactor,
            numPanes: 1
          },
          {
            id: 'bay-center',
            label: `Centre Bay (${pricing.bayCenterMm}mm)`,
            widthM: wCenter,
            x: 0,
            z: zCenter,
            rotY: 0,
            stackMode: state.stacking === 'right-stack' ? 'right-stack' : 'left-stack',
            trimLeftM: mitreFactor,
            trimRightM: mitreFactor,
            numPanes: 2
          },
          {
            id: 'bay-right',
            label: `Right Wing (${pricing.bayRightMm}mm)`,
            widthM: wRight,
            x: (vR.x + vROuter.x) * 0.5,
            z: (vR.z + vROuter.z) * 0.5,
            rotY: -bayAngle,
            stackMode: state.stacking === 'right-stack' ? 'right-stack' : 'left-stack',
            trimLeftM: mitreFactor,
            trimRightM: 0,
            numPanes: 1
          }
        ],
        bends: [
          { x: vL.x, z: vL.z, bisectorX: sinA * 0.5, bisectorZ: (1 + cosA) * 0.5, angleRad: bayAngle },
          { x: vR.x, z: vR.z, bisectorX: -sinA * 0.5, bisectorZ: (1 + cosA) * 0.5, angleRad: bayAngle }
        ]
      };
    }

    if (state.windowPreset === 'corner-window') {
      // 90° Internal Corner Window: Left Return Wall (+46° yaw) + Right Main Wall (-44° yaw) -> exact 90.0° right angle!
      const rotLeft = Math.PI * 0.255; // +45.9°
      const rotRight = -Math.PI * 0.245; // -44.1° -> difference = 90.0°
      const wLeft = Math.max(1.15, Math.min(2.25, pricing.cornerLeftMm * scaleMPerMm));
      const wRight = Math.max(1.25, Math.min(2.40, pricing.cornerRightMm * scaleMPerMm));

      const vCorner = { x: (wLeft - wRight) * 0.20, z: -0.58 };
      const vLOuter = {
        x: vCorner.x - wLeft * Math.cos(rotLeft),
        z: vCorner.z + wLeft * Math.sin(rotLeft)
      };
      const vROuter = {
        x: vCorner.x + wRight * Math.cos(rotRight),
        z: vCorner.z - wRight * Math.sin(rotRight)
      };

      // Mitre trim factor tan(90° / 2) = 1.0 so offset curtain/track planes meet at the 90° internal corner
      const mitreFactor = 1.0;

      return {
        preset: 'corner-window',
        vLOuter,
        vCorner,
        vROuter,
        rotLeft,
        rotRight,
        segments: [
          {
            id: 'corner-left',
            label: `Left Return (${pricing.cornerLeftMm}mm)`,
            widthM: wLeft,
            x: (vLOuter.x + vCorner.x) * 0.5,
            z: (vLOuter.z + vCorner.z) * 0.5,
            rotY: rotLeft,
            stackMode: state.stacking === 'right-stack' ? 'right-stack' : 'left-stack',
            trimLeftM: 0,
            trimRightM: mitreFactor,
            numPanes: 2
          },
          {
            id: 'corner-right',
            label: `Right Wall (${pricing.cornerRightMm}mm)`,
            widthM: wRight,
            x: (vCorner.x + vROuter.x) * 0.5,
            z: (vCorner.z + vROuter.z) * 0.5,
            rotY: rotRight,
            stackMode: state.stacking === 'left-stack' ? 'left-stack' : 'right-stack',
            trimLeftM: mitreFactor,
            trimRightM: 0,
            numPanes: 2
          }
        ],
        bends: [
          {
            x: vCorner.x,
            z: vCorner.z,
            bisectorX: (Math.sin(rotLeft) + Math.sin(rotRight)) * 0.707,
            bisectorZ: (Math.cos(rotLeft) + Math.cos(rotRight)) * 0.707,
            angleRad: Math.PI * 0.5
          }
        ]
      };
    }

    // Standard Straight Window / Sliding Door / Home Theatre Wall
    return {
      preset: state.windowPreset || 'sliding-door',
      segments: [
        {
          id: 'main',
          label: `${pricing.widthMm}mm Window`,
          widthM,
          x: 0,
          z: 0,
          rotY: 0,
          stackMode: state.stacking || 'centre-open',
          trimLeftM: 0,
          trimRightM: 0,
          numPanes: state.windowPreset === 'sliding-door' || state.windowPreset === 'home-theatre' ? 3 : 2
        }
      ],
      bends: []
    };
  }

  /**
   * Build Full Interactive 3D Architectural Room Interior in Three.js
   * Supports:
   * - Straight Window / Sliding Door (recessed window frame, return walls, ceiling cornice, oak floor, woven rug, 3D sofa silhouette, distant curved outdoor garden panorama)
   * - 3-Sided Bay Window (3 angled bay window frames at 135°, built-in 3D upholstered bay window seat & cushions, flanking walls)
   * - 90° Corner Window (L-shaped perpendicular walls & windows meeting at a 90° structural corner post + designer bouclé ottoman)
   * - Home Theatre Cinema Wall (dark acoustic timber slat panels, plush cinema carpet, warm wall sconce lighting)
   */
  function build3DStudioRoomArchitecture(webgl, widthM, dropM, state, pricing, layout) {
    const { THREE, roomEnvGroup } = webgl;
    while (roomEnvGroup.children.length > 0) {
      roomEnvGroup.remove(roomEnvGroup.children[0]);
    }

    const isTheatre = state.lighting === 'theatre' || state.windowPreset === 'home-theatre';
    const isEvening = state.lighting === 'evening';

    const wallColor = isTheatre ? 0x1D1F24 : isEvening ? 0xE2D8C8 : 0xF4F0E8;
    const wallMat = new THREE.MeshStandardMaterial({
      color: wallColor,
      roughness: 0.86,
      metalness: 0.02
    });

    const trimMat = new THREE.MeshStandardMaterial({
      color: isTheatre ? 0x15171B : 0xFAF8F4,
      roughness: 0.48,
      metalness: 0.03
    });

    const frameMat = new THREE.MeshStandardMaterial({
      color: 0x232528,
      roughness: 0.34,
      metalness: 0.58
    });

    const glassMat = new THREE.MeshPhysicalMaterial({
      color: isTheatre ? 0x1B2330 : 0xEBF4FA,
      transparent: true,
      opacity: isTheatre ? 0.28 : 0.14,
      roughness: 0.08,
      metalness: 0.05,
      reflectivity: 0.85,
      clearcoat: 0.9
    });

    const oakWoodMat = new THREE.MeshStandardMaterial({
      color: isTheatre ? 0x3A2E25 : 0xC6A279,
      roughness: 0.42,
      metalness: 0.04
    });

    const floorY = -dropM * 0.5 - 0.008;
    const ceilingY = dropM * 0.5 + 0.48;
    const roomHeight = ceilingY - floorY;
    const midRoomY = (ceilingY + floorY) * 0.5;

    // 1. Curved 3D Outdoor Garden & Sky Panorama Dome (distant z = -1.55m for real 3D orbit parallax)
    let outdoorMat;
    if (ASSET_CACHE.roomPhotos.outdoor) {
      if (!ASSET_CACHE.threeTex.outdoor) {
        const t = new THREE.Texture(ASSET_CACHE.roomPhotos.outdoor);
        t.colorSpace = THREE.SRGBColorSpace;
        t.needsUpdate = true;
        ASSET_CACHE.threeTex.outdoor = t;
      }
      outdoorMat = new THREE.MeshBasicMaterial({
        map: ASSET_CACHE.threeTex.outdoor,
        color: isTheatre ? 0x2E3B52 : isEvening ? 0xDFAF8C : 0xFFFFFF,
        side: THREE.DoubleSide
      });
    } else {
      outdoorMat = new THREE.MeshBasicMaterial({
        color: isTheatre ? 0x1C2536 : 0xCFE4F5,
        side: THREE.DoubleSide
      });
    }
    const outdoorArc = new THREE.Mesh(
      new THREE.CylinderGeometry(4.8, 4.8, 4.2, 40, 1, true, Math.PI * 0.62, Math.PI * 0.76),
      outdoorMat
    );
    outdoorArc.scale.set(-1, 1, 1);
    outdoorArc.position.set(0, 0.15, 2.35);
    roomEnvGroup.add(outdoorArc);

    // 2. Helper to build a 3D Recessed Window Segment (lintel, mullions, sill, glass) in local segment coords
    const buildWindowSegmentWall = (seg, opts = {}) => {
      const segGroup = new THREE.Group();
      segGroup.position.set(seg.x, 0, seg.z);
      segGroup.rotation.y = seg.rotY;

      const winW = seg.widthM * 0.96;
      const winH = opts.isBenchBay ? dropM * 0.78 : dropM * 0.98;
      const winCenterY = opts.isBenchBay ? dropM * 0.10 : 0.0;
      const winBottomY = winCenterY - winH * 0.5;
      const winTopY = winCenterY + winH * 0.5;

      // Top wall lintel above window up to ceiling
      const lintelH = Math.max(0.2, ceilingY - winTopY + 0.1);
      const topLintel = new THREE.Mesh(new THREE.BoxGeometry(seg.widthM + 0.16, lintelH, 0.22), wallMat);
      topLintel.position.set(0, winTopY + lintelH * 0.5, -0.15);
      topLintel.receiveShadow = true;
      segGroup.add(topLintel);

      // Ceiling cornice along this wall segment
      const cornice = new THREE.Mesh(new THREE.BoxGeometry(seg.widthM + 0.20, 0.065, 0.065), trimMat);
      cornice.position.set(0, ceilingY - 0.03, -0.03);
      segGroup.add(cornice);

      // Lower wall spandrel & sill (if window doesn't go to floor)
      if (winBottomY > floorY + 0.05) {
        const spandrelH = winBottomY - floorY;
        const spandrel = new THREE.Mesh(new THREE.BoxGeometry(seg.widthM + 0.12, spandrelH, 0.22), wallMat);
        spandrel.position.set(0, floorY + spandrelH * 0.5, -0.15);
        spandrel.receiveShadow = true;
        segGroup.add(spandrel);

        // Bevelled interior window sill
        const sill = new THREE.Mesh(new THREE.BoxGeometry(winW + 0.06, 0.032, 0.18), trimMat);
        sill.position.set(0, winBottomY - 0.01, -0.10);
        sill.receiveShadow = true;
        segGroup.add(sill);
      }

      // Narrow side reveal pillars on outer edges of segment
      const jambW = Math.max(0.03, (seg.widthM - winW) * 0.5 + 0.02);
      const leftJamb = new THREE.Mesh(new THREE.BoxGeometry(jambW, winH + 0.04, 0.22), wallMat);
      leftJamb.position.set(-winW * 0.5 - jambW * 0.5 + 0.01, winCenterY, -0.15);
      const rightJamb = new THREE.Mesh(new THREE.BoxGeometry(jambW, winH + 0.04, 0.22), wallMat);
      rightJamb.position.set(winW * 0.5 + jambW * 0.5 - 0.01, winCenterY, -0.15);
      segGroup.add(leftJamb, rightJamb);

      // Architectural Charcoal Aluminium Mullions & Rails
      const panes = seg.numPanes || 2;
      for (let m = 0; m <= panes; m++) {
        const mx = -winW * 0.5 + (winW * m) / panes;
        const mullion = new THREE.Mesh(new THREE.BoxGeometry(0.034, winH + 0.02, 0.055), frameMat);
        mullion.position.set(mx, winCenterY, -0.14);
        segGroup.add(mullion);
      }
      const topRail = new THREE.Mesh(new THREE.BoxGeometry(winW + 0.04, 0.036, 0.06), frameMat);
      topRail.position.set(0, winTopY, -0.14);
      const botRail = new THREE.Mesh(new THREE.BoxGeometry(winW + 0.04, 0.036, 0.06), frameMat);
      botRail.position.set(0, winBottomY, -0.14);
      segGroup.add(topRail, botRail);

      // Subtle architectural transom bar at upper 28% for tall windows
      const transomY = winCenterY + winH * 0.24;
      const transom = new THREE.Mesh(new THREE.BoxGeometry(winW, 0.022, 0.045), frameMat);
      transom.position.set(0, transomY, -0.14);
      segGroup.add(transom);

      // Specular architectural glass pane
      const glassPane = new THREE.Mesh(new THREE.PlaneGeometry(winW, winH), glassMat);
      glassPane.position.set(0, winCenterY, -0.155);
      segGroup.add(glassPane);

      roomEnvGroup.add(segGroup);
    };

    // 3. Build Preset-Specific 3D Room Architecture
    if (layout.preset === 'bay-window') {
      // Build all 3 angled bay window walls (Left Wing +45°, Centre Bay 0°, Right Wing -45°)
      layout.segments.forEach(seg => {
        buildWindowSegmentWall(seg, { isBenchBay: false });
      });

      // Structural angled vertical bay corner posts at vL and vR
      [layout.vL, layout.vR].forEach(v => {
        const post = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, roomHeight, 12), frameMat);
        post.position.set(v.x, midRoomY, v.z - 0.13);
        roomEnvGroup.add(post);
      });

      // Flanking Left & Right Main Room Walls outside the bay alcove
      const sideWallW = 2.4;
      const leftMainWall = new THREE.Mesh(new THREE.BoxGeometry(sideWallW, roomHeight, 0.24), wallMat);
      leftMainWall.position.set(layout.vLOuter.x - sideWallW * 0.5, midRoomY, layout.vLOuter.z - 0.12);
      leftMainWall.receiveShadow = true;
      const rightMainWall = new THREE.Mesh(new THREE.BoxGeometry(sideWallW, roomHeight, 0.24), wallMat);
      rightMainWall.position.set(layout.vROuter.x + sideWallW * 0.5, midRoomY, layout.vROuter.z - 0.12);
      rightMainWall.receiveShadow = true;
      roomEnvGroup.add(leftMainWall, rightMainWall);

      // Skirting boards on flanking walls
      const skirtL = new THREE.Mesh(new THREE.BoxGeometry(sideWallW, 0.09, 0.03), trimMat);
      skirtL.position.set(layout.vLOuter.x - sideWallW * 0.5, floorY + 0.045, layout.vLOuter.z + 0.01);
      const skirtR = new THREE.Mesh(new THREE.BoxGeometry(sideWallW, 0.09, 0.03), trimMat);
      skirtR.position.set(layout.vROuter.x + sideWallW * 0.5, floorY + 0.045, layout.vROuter.z + 0.01);
      roomEnvGroup.add(skirtL, skirtR);
    } else if (layout.preset === 'corner-window') {
      // Build both perpendicular 90° Corner Window walls
      layout.segments.forEach(seg => {
        buildWindowSegmentWall(seg, { isBenchBay: false });
      });

      // Structural 90° Corner Mullion Post at vCorner
      const cornerPost = new THREE.Mesh(new THREE.BoxGeometry(0.085, roomHeight, 0.085), frameMat);
      cornerPost.position.set(layout.vCorner.x, midRoomY, layout.vCorner.z - 0.11);
      cornerPost.rotation.y = layout.rotLeft;
      roomEnvGroup.add(cornerPost);

      // Outer return wall extensions beyond the L-window
      const extLen = 2.2;
      const cosL = Math.cos(layout.rotLeft);
      const sinL = Math.sin(layout.rotLeft);
      const leftExt = new THREE.Mesh(new THREE.BoxGeometry(extLen, roomHeight, 0.24), wallMat);
      leftExt.position.set(
        layout.vLOuter.x - extLen * 0.5 * cosL - 0.12 * sinL,
        midRoomY,
        layout.vLOuter.z + extLen * 0.5 * sinL - 0.12 * cosL
      );
      leftExt.rotation.y = layout.rotLeft;
      leftExt.receiveShadow = true;

      const cosR = Math.cos(layout.rotRight);
      const sinR = Math.sin(layout.rotRight);
      const rightExt = new THREE.Mesh(new THREE.BoxGeometry(extLen, roomHeight, 0.24), wallMat);
      rightExt.position.set(
        layout.vROuter.x + extLen * 0.5 * cosR - 0.12 * sinR,
        midRoomY,
        layout.vROuter.z - extLen * 0.5 * sinR - 0.12 * cosR
      );
      rightExt.rotation.y = layout.rotRight;
      rightExt.receiveShadow = true;
      roomEnvGroup.add(leftExt, rightExt);
    } else {
      // Straight Window / Sliding Door / Home Theatre
      const mainSeg = layout.segments[0];
      buildWindowSegmentWall(mainSeg, { isBenchBay: false });

      const backWallW = 8.4;
      const winW = mainSeg.widthM * 0.96;
      const sideW = Math.max(0.8, (backWallW - winW) * 0.5);

      const leftWall = new THREE.Mesh(new THREE.BoxGeometry(sideW, roomHeight, 0.24), wallMat);
      leftWall.position.set(-winW * 0.5 - sideW * 0.5, midRoomY, -0.15);
      leftWall.receiveShadow = true;

      const rightWall = new THREE.Mesh(new THREE.BoxGeometry(sideW, roomHeight, 0.24), wallMat);
      rightWall.position.set(winW * 0.5 + sideW * 0.5, midRoomY, -0.15);
      rightWall.receiveShadow = true;
      roomEnvGroup.add(leftWall, rightWall);

      // Left & Right 3D Return Side Walls extending forward into the room
      const returnWallDepth = 5.2;
      const roomHalfSpan = Math.max(2.65, winW * 0.5 + 0.95);
      const returnLeft = new THREE.Mesh(new THREE.BoxGeometry(0.22, roomHeight, returnWallDepth), wallMat);
      returnLeft.position.set(-roomHalfSpan, midRoomY, returnWallDepth * 0.5 - 0.25);
      returnLeft.receiveShadow = true;

      const returnRight = new THREE.Mesh(new THREE.BoxGeometry(0.22, roomHeight, returnWallDepth), wallMat);
      returnRight.position.set(roomHalfSpan, midRoomY, returnWallDepth * 0.5 - 0.25);
      returnRight.receiveShadow = true;
      roomEnvGroup.add(returnLeft, returnRight);

      // Skirting boards along back & side walls
      const skirtL = new THREE.Mesh(new THREE.BoxGeometry(sideW, 0.09, 0.028), trimMat);
      skirtL.position.set(-winW * 0.5 - sideW * 0.5, floorY + 0.045, -0.02);
      const skirtR = new THREE.Mesh(new THREE.BoxGeometry(sideW, 0.09, 0.028), trimMat);
      skirtR.position.set(winW * 0.5 + sideW * 0.5, floorY + 0.045, -0.02);
      const skirtSideL = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.09, returnWallDepth), trimMat);
      skirtSideL.position.set(-roomHalfSpan + 0.12, floorY + 0.045, returnWallDepth * 0.5 - 0.25);
      const skirtSideR = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.09, returnWallDepth), trimMat);
      skirtSideR.position.set(roomHalfSpan - 0.12, floorY + 0.045, returnWallDepth * 0.5 - 0.25);
      roomEnvGroup.add(skirtL, skirtR, skirtSideL, skirtSideR);

      if (isTheatre) {
        // Acoustic timber slat panels on flanking walls in Home Theatre mode
        const slatMat = new THREE.MeshStandardMaterial({ color: 0x4A3728, roughness: 0.55 });
        for (let s = 0; s < 7; s++) {
          const offset = 0.22 + s * 0.08;
          const slatL = new THREE.Mesh(new THREE.BoxGeometry(0.035, dropM * 0.88, 0.03), slatMat);
          slatL.position.set(-winW * 0.5 - offset, 0, -0.02);
          const slatR = new THREE.Mesh(new THREE.BoxGeometry(0.035, dropM * 0.88, 0.03), slatMat);
          slatR.position.set(winW * 0.5 + offset, 0, -0.02);
          roomEnvGroup.add(slatL, slatR);
        }
      }
    }

    // 4. Ceiling Plane above the 3D Room
    const ceilingMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(9.6, 7.0),
      new THREE.MeshStandardMaterial({ color: isTheatre ? 0x16171B : 0xFAF8F4, roughness: 0.9 })
    );
    ceilingMesh.rotation.x = Math.PI * 0.5;
    ceilingMesh.position.set(0, ceilingY, 1.4);
    roomEnvGroup.add(ceilingMesh);

    // 5. Hardwood Tasmanian Oak Floor (or dark plush cinema carpet) + Textured Area Rug
    let floorMat;
    if (!isTheatre && ASSET_CACHE.roomPhotos.floorOak) {
      if (!ASSET_CACHE.threeTex.floorOak) {
        const ft = new THREE.Texture(ASSET_CACHE.roomPhotos.floorOak);
        ft.colorSpace = THREE.SRGBColorSpace;
        ft.wrapS = THREE.RepeatWrapping;
        ft.wrapT = THREE.RepeatWrapping;
        ft.repeat.set(3.4, 2.6);
        ft.needsUpdate = true;
        ASSET_CACHE.threeTex.floorOak = ft;
      }
      floorMat = new THREE.MeshStandardMaterial({
        map: ASSET_CACHE.threeTex.floorOak,
        color: isEvening ? 0xD4B99B : 0xF5E8D8,
        roughness: 0.40,
        metalness: 0.05
      });
    } else {
      floorMat = new THREE.MeshStandardMaterial({
        color: isTheatre ? 0x22201F : 0xB89872,
        roughness: isTheatre ? 0.92 : 0.44,
        metalness: 0.04
      });
    }
    const floorMesh = new THREE.Mesh(new THREE.PlaneGeometry(9.6, 7.0), floorMat);
    floorMesh.rotation.x = -Math.PI * 0.5;
    floorMesh.position.set(0, floorY, 1.6);
    floorMesh.receiveShadow = true;
    roomEnvGroup.add(floorMesh);

    // Woven Architectural Floor Rug in front of the window
    const rugMat = new THREE.MeshStandardMaterial({
      color: isTheatre ? 0x2C2927 : 0xE6DECF,
      roughness: 0.92,
      metalness: 0.0
    });
    const rugMesh = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.012, 2.1), rugMat);
    rugMesh.position.set(0, floorY + 0.006, 1.35);
    rugMesh.receiveShadow = true;
    roomEnvGroup.add(rugMesh);
  }

  /**
   * Build a Three.js MeshPhysicalMaterial using the real Twinbru 4K-to-512px PBR Texture Maps
   */
  function createTwinbruPbrMaterial(THREE, {
    fabric,
    swatchHex,
    isSheer,
    widthM,
    dropM,
    isMacro,
    backlightBoost = 1.0
  }) {
    const repeatX = Math.max(2, (widthM * 1.45) / (fabric.repeatM || 0.26));
    const repeatY = Math.max(2, dropM / (fabric.repeatM || 0.26));

    const diffMap = getPbrTileTexture(THREE, 'd', fabric.pbrTile, repeatX, repeatY);
    const normMap = getPbrTileTexture(THREE, 'n', fabric.pbrTile, repeatX, repeatY);
    const roughMap = getPbrTileTexture(THREE, 'r', fabric.pbrTile, repeatX, repeatY);
    const alphaMap = isSheer ? getPbrTileTexture(THREE, 'a', fabric.pbrTile, repeatX, repeatY) : null;

    const baseColor = new THREE.Color(swatchHex);
    const normalStrength = isMacro ? 2.15 : 1.42;

    if (isSheer) {
      // Sheer Voile / Linen / Bouclé Sheer with real yarn transparency & window backlight glow
      const baseOpacity = Math.min(0.82, Math.max(0.38, (fabric.sheerOpacity || 0.48) * (1.06 / Math.max(0.75, backlightBoost))));
      const emissiveColor = baseColor.clone().lerp(new THREE.Color(0xFFFDF8), 0.55);

      return new THREE.MeshPhysicalMaterial({
        color: baseColor,
        map: diffMap,
        normalMap: normMap,
        normalScale: new THREE.Vector2(normalStrength * 0.85, normalStrength * 0.85),
        roughnessMap: roughMap,
        roughness: fabric.roughness || 0.80,
        metalness: 0.0,
        alphaMap: alphaMap,
        transparent: true,
        opacity: baseOpacity,
        depthWrite: false,
        side: THREE.DoubleSide,
        sheen: fabric.sheen || 0.42,
        sheenColor: new THREE.Color(0xFFFFFF),
        sheenRoughness: 0.65,
        emissive: emissiveColor,
        emissiveIntensity: 0.14 * backlightBoost
      });
    }

    const isVelvet = fabric.category === 'velvet';
    const sheenColor = isVelvet
      ? baseColor.clone().lerp(new THREE.Color(0xFFF6E5), 0.48)
      : baseColor.clone().lerp(new THREE.Color(0xFFFFFF), 0.28);

    return new THREE.MeshPhysicalMaterial({
      color: baseColor,
      map: diffMap,
      normalMap: normMap,
      normalScale: new THREE.Vector2(normalStrength, normalStrength),
      roughnessMap: roughMap,
      roughness: fabric.roughness || (isVelvet ? 0.42 : 0.76),
      metalness: 0.02,
      side: THREE.DoubleSide,
      sheen: fabric.sheen || (isVelvet ? 1.0 : 0.52),
      sheenColor,
      sheenRoughness: isVelvet ? 0.25 : 0.64,
      clearcoat: 0.0
    });
  }

  /**
   * Populate the Three.js scene with the complete 3D PBR Curtain / Roman Blind + Track assembly
   * Supports Straight, 3-Sided Bay (3 angled segments + 2-bend track), and 90° Corner (2 perpendicular segments + 1-bend L-track).
   */
  function renderWebGLTreatmentScene(webgl, state, pricing, renderMode) {
    // renderMode: 'overlay' (transparent background for AR compositing) | 'studio3d' (full interactive 3D room environment)
    const {
      THREE,
      renderer,
      scene,
      camera,
      controls,
      hemiLight,
      keyLight,
      windowBackLight,
      fillLight,
      rootGroup,
      roomEnvGroup
    } = webgl;

    while (rootGroup.children.length > 0) {
      const child = rootGroup.children[0];
      rootGroup.remove(child);
    }
    while (roomEnvGroup.children.length > 0) {
      roomEnvGroup.remove(roomEnvGroup.children[0]);
    }

    const pleat = getPleatById(state.pleatId);
    const primaryFab = getFabricById(state.primaryFabricId);
    const primarySwatch = primaryFab.swatches.find(s => s.id === state.primarySwatchId) || primaryFab.swatches[0];
    const secondaryFab = getFabricById(state.secondaryFabricId || 'zepel-flo-ii');
    const secondarySwatch = secondaryFab.swatches.find(s => s.id === state.secondarySwatchId) || secondaryFab.swatches[0];

    // Physical aspect scaled around a reference 2.15m drop in 3D space
    const dropM = 2.15;
    const rawAspect = Math.max(0.75, Math.min(2.35, pricing.widthMm / Math.max(900, pricing.dropMm)));
    const widthM = renderMode === 'overlay'
      ? 2.65
      : dropM * rawAspect;

    const drawPct = Math.max(0.15, Math.min(1.0, (Number(state.drawPercent) || 78) / 100));
    const isMacro = state.cameraView === 'header' || state.cameraView === 'weave';

    const isTheatre = state.lighting === 'theatre' || (state.mode === '3d' && state.windowPreset === 'home-theatre');
    const isEvening = state.lighting === 'evening';
    const arBrightness = state.arSampledBrightness || 1.0;

    if (isTheatre) {
      hemiLight.color.setHex(0xB8C6E0);
      hemiLight.groundColor.setHex(0x28221E);
      hemiLight.intensity = 0.55;
      keyLight.color.setHex(0xFFE0B2);
      keyLight.intensity = 1.25;
      windowBackLight.intensity = 0.45;
      fillLight.intensity = 0.42;
    } else if (isEvening) {
      hemiLight.color.setHex(0xFFE4C4);
      hemiLight.groundColor.setHex(0x785E46);
      hemiLight.intensity = 0.78;
      keyLight.color.setHex(0xFFD29D);
      keyLight.intensity = 1.55;
      windowBackLight.intensity = 0.85;
      fillLight.intensity = 0.62;
    } else {
      hemiLight.color.setHex(0xFFFDF8);
      hemiLight.groundColor.setHex(0xA6937C);
      hemiLight.intensity = 0.98 * arBrightness;
      keyLight.color.setHex(0xFFF8EC);
      keyLight.intensity = 1.85 * arBrightness;
      windowBackLight.intensity = 1.55 * arBrightness;
      fillLight.intensity = 0.72 * arBrightness;
    }

    const layout = compute3DWindowLayout(state, pricing, widthM, dropM, renderMode);

    if (renderMode === 'studio3d') {
      scene.background = new THREE.Color(isTheatre ? 0x16181D : isEvening ? 0xD8CFC0 : 0xECE7DE);
      build3DStudioRoomArchitecture(webgl, widthM, dropM, state, pricing, layout);
    } else {
      scene.background = null;
      renderer.setClearColor(0x000000, 0);
    }

    const totalTrackSpanM = layout.segments.reduce((acc, s) => acc + s.widthM, 0);

    // Helper to populate a single 3D segment (Straight, Bay Wing, Bay Centre, or 90° Corner Wall)
    const addSegmentTreatment = (seg, segIndex) => {
      const segGroup = new THREE.Group();
      segGroup.position.set(seg.x, 0, seg.z);
      segGroup.rotation.y = seg.rotY;
      rootGroup.add(segGroup);

      const segStartM = layout.segments.slice(0, segIndex).reduce((acc, s) => acc + s.widthM, 0);
      const segEndM = segStartM + seg.widthM;

      const addCurtainLayerToSeg = ({
        fabric,
        swatchHex,
        layerPleatId,
        layerDrawPct,
        zOffset,
        isSheer
      }) => {
        // Trim inner corner edges by zOffset * mitreFactor so angled bay & 90° corner curtains meet without overlapping
        const trimL = Math.max(0, zOffset) * (seg.trimLeftM || 0);
        const trimR = Math.max(0, zOffset) * (seg.trimRightM || 0);
        const effWidthM = Math.max(0.35, seg.widthM - trimL - trimR);
        const centerShiftX = (trimL - trimR) * 0.5;

        const mat = createTwinbruPbrMaterial(THREE, {
          fabric,
          swatchHex,
          isSheer,
          widthM: effWidthM,
          dropM,
          isMacro,
          backlightBoost: isTheatre ? 0.65 : isEvening ? 0.85 : 1.15 * arBrightness
        });

        const stackOpt = seg.stackMode || state.stacking || 'centre-open';
        const panels = [];

        if (layout.segments.length > 1) {
          const dir = layout.preset === 'bay-window' ? 'centre-open' : (state.stacking || 'centre-open');
          if (dir === 'centre-open') {
            // 2 Continuous Halves: Left Half opens to far left end (0), Right Half opens to far right end (totalTrackSpanM)
            const halfSpanM = totalTrackSpanM * 0.5;
            let halfDrawnM;
            if (layout.preset === 'bay-window') {
              const outerWingM = Math.min(
                layout.segments[0].widthM,
                layout.segments[layout.segments.length - 1].widthM
              );
              const minStackM = outerWingM * 0.34;
              const normT = Math.max(0, Math.min(1, (layerDrawPct - 0.15) / 0.85));
              halfDrawnM = minStackM + (halfSpanM - minStackM) * Math.pow(normT, 1.55);
            } else {
              halfDrawnM = halfSpanM * Math.max(0.20, layerDrawPct);
            }

            // Left Half interval [0, halfDrawnM] intersected with segment [segStartM, segEndM]
            const leftEnd = Math.min(segEndM, halfDrawnM);
            const coveredLeftM = Math.max(0, leftEnd - segStartM);
            if (coveredLeftM > 0.03) {
              const fracL = Math.min(1.0, coveredLeftM / seg.widthM);
              const pwL = effWidthM * fracL;
              panels.push({ xCenter: centerShiftX - effWidthM * 0.5 + pwL * 0.5, panelW: pwL, sideSign: -1, frac: fracL, isOuterWing: seg.id === 'bay-left' });
            }

            // Right Half interval [totalTrackSpanM - halfDrawnM, totalTrackSpanM] intersected with segment [segStartM, segEndM]
            const rightStart = Math.max(segStartM, totalTrackSpanM - halfDrawnM);
            const coveredRightM = Math.max(0, segEndM - rightStart);
            if (coveredRightM > 0.03) {
              const fracR = Math.min(1.0, coveredRightM / seg.widthM);
              const pwR = effWidthM * fracR;
              panels.push({ xCenter: centerShiftX + effWidthM * 0.5 - pwR * 0.5, panelW: pwR, sideSign: 1, frac: fracR, isOuterWing: seg.id === 'bay-right' });
            }
          } else if (dir === 'left-stack') {
            const drawnTrackM = totalTrackSpanM * Math.max(0.22, layerDrawPct);
            const coveredM = Math.max(0, Math.min(seg.widthM, drawnTrackM - segStartM));
            if (coveredM > 0.04) {
              const frac = Math.min(1.0, coveredM / seg.widthM);
              const pw = effWidthM * frac;
              panels.push({ xCenter: centerShiftX - effWidthM * 0.5 + pw * 0.5, panelW: pw, sideSign: -1, frac });
            }
          } else {
            const drawnTrackM = totalTrackSpanM * Math.max(0.22, layerDrawPct);
            const coveredM = Math.max(0, Math.min(seg.widthM, segEndM - (totalTrackSpanM - drawnTrackM)));
            if (coveredM > 0.04) {
              const frac = Math.min(1.0, coveredM / seg.widthM);
              const pw = effWidthM * frac;
              panels.push({ xCenter: centerShiftX + effWidthM * 0.5 - pw * 0.5, panelW: pw, sideSign: 1, frac });
            }
          }
        } else if (stackOpt === 'left-stack') {
          const pw = effWidthM * Math.max(0.24, layerDrawPct);
          panels.push({ xCenter: centerShiftX - effWidthM * 0.5 + pw * 0.5, panelW: pw, sideSign: -1, frac: 1.0 });
        } else if (stackOpt === 'right-stack') {
          const pw = effWidthM * Math.max(0.24, layerDrawPct);
          panels.push({ xCenter: centerShiftX + effWidthM * 0.5 - pw * 0.5, panelW: pw, sideSign: 1, frac: 1.0 });
        } else {
          const halfW = effWidthM * Math.max(0.16, layerDrawPct * 0.5);
          panels.push({ xCenter: centerShiftX - effWidthM * 0.5 + halfW * 0.5, panelW: halfW, sideSign: -1, frac: 0.5 });
          panels.push({ xCenter: centerShiftX + effWidthM * 0.5 - halfW * 0.5, panelW: halfW, sideSign: 1, frac: 0.5 });
        }

        if (panels.length === 0) return;

        const segRatio = effWidthM / Math.max(1.2, widthM);
        const totalFolds = Math.max(8, Math.min(28, Math.round((pricing.widthMm * segRatio) / (layerPleatId === 'triple-pinch' ? 155 : 175))));

        panels.forEach(p => {
          const foldsPerPanel = p.isOuterWing
            ? Math.max(7, Math.round(totalFolds * Math.max(0.65, p.frac || 0.5)))
            : Math.max(4, Math.round(totalFolds * (p.frac || (1 / panels.length))));
          const effStackFactor = p.isOuterWing ? Math.max(0.35, 1.0 - layerDrawPct) : (1.0 - layerDrawPct);
          const geom = buildCurtainPanelGeometry(THREE, {
            panelWidthM: p.panelW,
            dropM,
            pleatId: layerPleatId,
            numFolds: foldsPerPanel,
            stackFactor: effStackFactor,
            isMacro,
            sideSign: p.sideSign
          });

          const mesh = new THREE.Mesh(geom, mat);
          mesh.position.set(p.xCenter, 0, zOffset);
          mesh.castShadow = !isSheer;
          mesh.receiveShadow = true;
          segGroup.add(mesh);

          const headerDetails = buildPinchHeaderDetails(THREE, {
            panelWidthM: p.panelW,
            dropM,
            pleatId: layerPleatId,
            numFolds: foldsPerPanel,
            stackFactor: effStackFactor,
            material: mat
          });
          headerDetails.position.set(p.xCenter, 0, zOffset);
          segGroup.add(headerDetails);
        });
      };

      if (pleat.isRoman) {
        const romanTrimL = 0.04 * (seg.trimLeftM || 0);
        const romanTrimR = 0.04 * (seg.trimRightM || 0);
        const romanW = Math.max(0.35, seg.widthM - romanTrimL - romanTrimR);
        const romanShiftX = (romanTrimL - romanTrimR) * 0.5;

        const isSheerRoman = primaryFab.category === 'sheer';
        const romanMat = createTwinbruPbrMaterial(THREE, {
          fabric: primaryFab,
          swatchHex: primarySwatch.hex,
          isSheer: isSheerRoman,
          widthM: romanW,
          dropM,
          isMacro,
          backlightBoost: arBrightness
        });
        const romanGroup = buildRomanBlindAssembly(THREE, {
          widthM: romanW,
          dropM,
          drawPct,
          fabricMaterial: romanMat,
          driveType: state.romanDrive || 'chain-easy-lift'
        });
        romanGroup.position.x = romanShiftX;
        segGroup.add(romanGroup);
      } else {
        // 1. Rear Sheer Layer on Double Track (z = -0.025)
        if (state.layerMode === 'double') {
          const sheerOffset = layout.preset === 'bay-window' ? 0.05 : 0.18;
          addCurtainLayerToSeg({
            fabric: secondaryFab,
            swatchHex: secondarySwatch.hex,
            layerPleatId: state.secondaryPleatId || 's-wave',
            layerDrawPct: Math.min(0.98, drawPct + sheerOffset),
            zOffset: -0.025,
            isSheer: true
          });
        }

        // 2. Primary Front Curtain Layer (z = +0.085)
        const isPrimarySheer = primaryFab.category === 'sheer';
        addCurtainLayerToSeg({
          fabric: primaryFab,
          swatchHex: primarySwatch.hex,
          layerPleatId: state.pleatId,
          layerDrawPct: drawPct,
          zOffset: 0.085,
          isSheer: isPrimarySheer
        });

        // 3. Architectural Track / Rod Hardware for this segment
        const trackTrimL = 0.085 * (seg.trimLeftM || 0);
        const trackTrimR = 0.085 * (seg.trimRightM || 0);
        const trackW = Math.max(0.35, seg.widthM - trackTrimL - trackTrimR);
        const trackShiftX = (trackTrimL - trackTrimR) * 0.5;

        const trackGroup = buildTrackHardwareGroup(THREE, {
          widthM: trackW,
          dropM,
          trackSeriesId: state.trackSeriesId || 'styletrack',
          trackFinishId: state.trackFinishId || 'black',
          layerMode: state.layerMode || 'double',
          mounting: state.mounting || 'top',
          bendsCount: 0
        });
        trackGroup.position.x = trackShiftX;
        segGroup.add(trackGroup);
      }
    };

    // Build all segments (1 for straight, 3 for 3-Sided Bay, 2 for 90° Corner)
    layout.segments.forEach((seg, idx) => addSegmentTreatment(seg, idx));

    // Add 3D Curved Corner Track Joiner Elbows for Bay Window (2 bends) and 90° Corner Window (1 bend)
    if (!pleat.isRoman && state.trackSeriesId !== 'none' && layout.bends.length > 0) {
      const series = TRACK_TABLES[state.trackSeriesId] || TRACK_TABLES.styletrack;
      const finish = series.finishes.find(f => f.id === state.trackFinishId) || series.finishes[0];
      const isMetallic = ['chrome', 'gold', 'satin', 'champagne', 'antique'].includes(finish.id);
      const elbowMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(finish.hex),
        roughness: isMetallic ? 0.24 : 0.42,
        metalness: isMetallic ? 0.85 : 0.25
      });
      const topY = dropM * 0.5 + 0.018;
      const zLayers = state.layerMode === 'double' ? [0.085, -0.025] : [0.085];

      layout.bends.forEach(b => {
        zLayers.forEach(zL => {
          const bx = b.x + b.bisectorX * zL;
          const bz = b.z + b.bisectorZ * zL;
          const joiner = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.026, 0.028, 18), elbowMat);
          joiner.position.set(bx, topY, bz);
          rootGroup.add(joiner);
        });
      });
    }

    // Configure Camera Position based on renderMode, cameraView, and windowPreset
    if (renderMode === 'overlay') {
      if (controls) controls.enabled = false;
      camera.fov = 26;
      camera.position.set(0, 0.01, 4.92);
      camera.lookAt(0, 0.01, 0.04);
      camera.updateProjectionMatrix();
    } else {
      camera.fov = 38;
      camera.updateProjectionMatrix();
      if (controls) controls.enabled = true;

      const cameraPresetKey = `${state.cameraView}_${state.windowPreset}`;
      if (webgl.lastPresetCamera !== cameraPresetKey) {
        webgl.lastPresetCamera = cameraPresetKey;
        if (state.cameraView === 'angle') {
          if (state.windowPreset === 'corner-window') {
            camera.position.set(1.35, 0.22, 3.85);
            controls?.target.set(0.0, -0.02, -0.18);
          } else if (state.windowPreset === 'bay-window') {
            camera.position.set(-1.35, 0.24, 3.95);
            controls?.target.set(0.0, -0.02, -0.16);
          } else {
            camera.position.set(-1.45, 0.22, 3.85);
            controls?.target.set(0, 0.0, 0.04);
          }
        } else if (state.cameraView === 'header') {
          const targetSeg = layout.segments[0];
          const focusX = targetSeg.x * 0.5 - 0.25;
          const focusZ = targetSeg.z + 0.07;
          camera.position.set(focusX - 0.22, dropM * 0.42, focusZ + 0.92);
          controls?.target.set(focusX, dropM * 0.38, focusZ);
        } else if (state.cameraView === 'weave') {
          const targetSeg = layout.segments[0];
          const focusX = targetSeg.x * 0.5 - 0.18;
          const focusZ = targetSeg.z + 0.08;
          camera.position.set(focusX - 0.10, 0.12, focusZ + 0.58);
          controls?.target.set(focusX, 0.08, focusZ);
        } else {
          // Default 'room' view: Full Interactive 3D Architectural Room Overview
          if (state.windowPreset === 'bay-window') {
            camera.position.set(0.0, 0.10, 4.35);
            controls?.target.set(0.0, -0.03, -0.22);
          } else if (state.windowPreset === 'corner-window') {
            camera.position.set(0.24, 0.12, 4.15);
            controls?.target.set(0.0, -0.03, -0.26);
          } else {
            camera.position.set(0.0, 0.06, 4.55);
            controls?.target.set(0.0, -0.02, 0.0);
          }
        }
        controls?.update();
      }
    }

    renderer.render(scene, camera);
  }

  /**
   * Warp the rendered 3D WebGL PBR transparent image onto a 4-corner perspective quad (TL, TR, BR, BL)
   * using high-density mesh subdivision + realistic wall/floor ambient occlusion shadows (Used in Mode 2: Window AR).
   */
  function drawWebGLQuadWithShadows(ctx, webglCanvas, quad, state, pricing) {
    const { tl, tr, br, bl } = quad;
    const pleat = getPleatById(state.pleatId);
    const W = webglCanvas.width;
    const H = webglCanvas.height;

    const pt = (u, v) => {
      const tx = tl.x + (tr.x - tl.x) * u;
      const ty = tl.y + (tr.y - tl.y) * u;
      const bx = bl.x + (br.x - bl.x) * u;
      const by = bl.y + (br.y - bl.y) * u;
      return { x: tx + (bx - tx) * v, y: ty + (by - ty) * v };
    };

    ctx.save();
    const sTL = pt(-0.01, -0.005);
    const sTR = pt(1.01, -0.005);
    const sBR = pt(1.01, 0.065);
    const sBL = pt(-0.01, 0.065);
    const topGrad = ctx.createLinearGradient(sTL.x, sTL.y, sBL.x, sBL.y);
    topGrad.addColorStop(0, 'rgba(12, 10, 8, 0.34)');
    topGrad.addColorStop(1, 'rgba(12, 10, 8, 0.0)');
    ctx.fillStyle = topGrad;
    ctx.beginPath();
    ctx.moveTo(sTL.x, sTL.y);
    ctx.lineTo(sTR.x, sTR.y);
    ctx.lineTo(sBR.x, sBR.y);
    ctx.lineTo(sBL.x, sBL.y);
    ctx.closePath();
    ctx.fill();

    if (!pleat.isRoman) {
      const fTL = pt(-0.02, 0.965);
      const fTR = pt(1.02, 0.965);
      const fBR = pt(1.03, 1.035);
      const fBL = pt(-0.03, 1.035);
      const floorGrad = ctx.createLinearGradient(fTL.x, fTL.y, fBL.x, fBL.y);
      floorGrad.addColorStop(0, 'rgba(15, 12, 9, 0.0)');
      floorGrad.addColorStop(0.45, 'rgba(15, 12, 9, 0.32)');
      floorGrad.addColorStop(1, 'rgba(15, 12, 9, 0.0)');
      ctx.fillStyle = floorGrad;
      ctx.beginPath();
      ctx.moveTo(fTL.x, fTL.y);
      ctx.lineTo(fTR.x, fTR.y);
      ctx.lineTo(fBR.x, fBR.y);
      ctx.lineTo(fBL.x, fBL.y);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();

    const srcX0 = W * 0.158;
    const srcY0 = H * 0.014;
    const srcW = W * 0.684;
    const srcH = H * 0.966;

    const cols = 24;
    const rows = 12;

    const drawTexturedTriangle = (p0, p1, p2, s0, s1, s2) => {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(p0.x, p0.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.closePath();
      ctx.clip();

      const denom = s0.x * (s2.y - s1.y) - s1.x * s2.y + s2.x * s1.y + (s1.x - s2.x) * s0.y;
      if (Math.abs(denom) > 1e-5) {
        const m11 = -(s0.y * (p2.x - p1.x) - s1.y * p2.x + s2.y * p1.x + (s1.y - s2.y) * p0.x) / denom;
        const m12 = (s1.y * p2.y + s0.y * (p1.y - p2.y) - s2.y * p1.y + (s2.y - s1.y) * p0.y) / denom;
        const m21 = (s0.x * (p2.x - p1.x) - s1.x * p2.x + s2.x * p1.x + (s1.x - s2.x) * p0.x) / denom;
        const m22 = -(s1.x * p2.y + s0.x * (p1.y - p2.y) - s2.x * p1.y + (s2.x - s1.x) * p0.y) / denom;
        const dx = (s0.x * (s2.y * p1.x - s1.y * p2.x) + s0.y * (s1.x * p2.x - s2.x * p1.x) + (s2.x * s1.y - s1.x * s2.y) * p0.x) / denom;
        const dy = (s0.x * (s2.y * p1.y - s1.y * p2.y) + s0.y * (s1.x * p2.y - s2.x * p1.y) + (s2.x * s1.y - s1.x * s2.y) * p0.y) / denom;

        ctx.transform(m11, m12, m21, m22, dx, dy);
        ctx.drawImage(webglCanvas, 0, 0);
      }
      ctx.restore();
    };

    for (let r = 0; r < rows; r++) {
      const v0 = r / rows;
      const v1 = (r + 1) / rows;
      const sy0 = srcY0 + v0 * srcH;
      const sy1 = srcY0 + v1 * srcH;

      for (let c = 0; c < cols; c++) {
        const u0 = c / cols;
        const u1 = (c + 1) / cols;
        const sx0 = srcX0 + u0 * srcW;
        const sx1 = srcX0 + u1 * srcW;

        const p00 = pt(u0, v0);
        const p10 = pt(u1, v0);
        const p11 = pt(u1, v1);
        const p01 = pt(u0, v1);

        const s00 = { x: sx0, y: sy0 };
        const s10 = { x: sx1, y: sy0 };
        const s11 = { x: sx1, y: sy1 };
        const s01 = { x: sx0, y: sy1 };

        drawTexturedTriangle(p00, p10, p11, s00, s10, s11);
        drawTexturedTriangle(p00, p11, p01, s00, s11, s01);
      }
    }
  }

  /**
   * Draw Photorealistic Architectural Room Scene Photo onto Canvas (Used in Mode 2: Window AR Sample Rooms)
   */
  function drawArchitecturalRoomPhoto(ctx, W, H, sceneType, lightingMode) {
    const photo = ASSET_CACHE.roomPhotos[sceneType] || ASSET_CACHE.roomPhotos.living;
    ctx.save();
    if (photo) {
      ctx.drawImage(photo, 0, 0, W, H);
      if (lightingMode === 'evening' && sceneType !== 'theatre') {
        ctx.fillStyle = 'rgba(218, 145, 72, 0.16)';
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = 'rgba(255, 190, 120, 0.08)';
        ctx.fillRect(0, 0, W, H);
      } else if (lightingMode === 'theatre' && sceneType !== 'theatre') {
        ctx.fillStyle = 'rgba(22, 26, 36, 0.58)';
        ctx.fillRect(0, 0, W, H);
      }
    } else {
      const wallGrad = ctx.createLinearGradient(0, 0, 0, H);
      wallGrad.addColorStop(0, '#F3EFE8');
      wallGrad.addColorStop(0.73, '#E5DDD0');
      wallGrad.addColorStop(0.731, '#C5A986');
      wallGrad.addColorStop(1, '#9E8262');
      ctx.fillStyle = wallGrad;
      ctx.fillRect(0, 0, W, H);
    }
    ctx.restore();
  }

  /**
   * Sample average luminance inside the AR window quad so sheer translucency & 3D PBR lighting
   * automatically adapt to the user's real window brightness.
   */
  function sampleWindowQuadBrightness(ctx, quad) {
    try {
      const cx = Math.round((quad.tl.x + quad.tr.x + quad.br.x + quad.bl.x) * 0.25);
      const cy = Math.round((quad.tl.y + quad.tr.y + quad.br.y + quad.bl.y) * 0.25);
      const sampleW = 48;
      const sampleH = 48;
      const sx = Math.max(0, Math.min(ctx.canvas.width - sampleW, cx - sampleW / 2));
      const sy = Math.max(0, Math.min(ctx.canvas.height - sampleH, cy - sampleH / 2));
      const data = ctx.getImageData(sx, sy, sampleW, sampleH).data;
      let sum = 0;
      for (let i = 0; i < data.length; i += 16) {
        sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      }
      const avg = sum / (data.length / 16);
      return Math.max(0.72, Math.min(1.28, 0.68 + (avg / 255) * 0.62));
    } catch {
      return 1.0;
    }
  }

  /**
   * Fallback 2D renderer only used in headless Node.js unit tests where WebGL / Three.js is absent
   */
  function renderFallback2DQuad(ctx, quad, state) {
    const { tl, tr, br, bl } = quad;
    const primaryFab = getFabricById(state.primaryFabricId);
    const primarySwatch = primaryFab.swatches.find(s => s.id === state.primarySwatchId) || primaryFab.swatches[0];
    const rgb = hexToRgb(primarySwatch.hex);
    ctx.save();
    ctx.fillStyle = rgbaStr(rgb, primaryFab.category === 'sheer' ? 0.55 : 0.95, 0.96);
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y);
    ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(br.x, br.y);
    ctx.lineTo(bl.x, bl.y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /**
   * Render Complete Scene onto Canvas (1280x720)
   * - Mode 1 ('3d'): Full Interactive 3D Three.js Room Architecture (Straight, 3-Sided Bay, 90° Corner, Home Theatre)
   * - Mode 2 ('ar'): "See on Your Window" 3D PBR AR Compositor with 4-corner perspective homography handles
   */
  function renderStudioCanvas(canvas, state, pricing, options = {}) {
    if (!canvas) return;
    canvas.__llLastRenderArgs = { state, pricing, options };

    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const pleat = getPleatById(state.pleatId);
    const webgl = getOrInitWebGLContext(canvas, canvas.closest?.('#ll-curtain-studio-root'));

    if (state.mode === 'ar') {
      // ======================================================================
      // MODE 2: "SEE ON YOUR WINDOW" 3D PBR AR COMPOSITING MODE
      // ======================================================================
      if (state.arSourceElement) {
        try {
          ctx.drawImage(state.arSourceElement, 0, 0, W, H);
        } catch {
          const sampleKey = state.arSampleId === 'bay-window-room' ? 'bay' : state.arSampleId === 'theatre-room' ? 'theatre' : 'living';
          drawArchitecturalRoomPhoto(ctx, W, H, sampleKey, state.lighting);
        }
      } else {
        const sampleKey = state.arSampleId === 'bay-window-room' ? 'bay' : state.arSampleId === 'theatre-room' ? 'theatre' : 'living';
        drawArchitecturalRoomPhoto(ctx, W, H, sampleKey, state.lighting);
      }

      const quad = {
        tl: { x: state.arCorners.tl.x * W, y: state.arCorners.tl.y * H },
        tr: { x: state.arCorners.tr.x * W, y: state.arCorners.tr.y * H },
        br: { x: state.arCorners.br.x * W, y: state.arCorners.br.y * H },
        bl: { x: state.arCorners.bl.x * W, y: state.arCorners.bl.y * H }
      };

      state.arSampledBrightness = sampleWindowQuadBrightness(ctx, quad);

      if (webgl) {
        renderWebGLTreatmentScene(webgl, state, pricing, 'overlay');
        drawWebGLQuadWithShadows(ctx, webgl.offscreen, quad, state, pricing);
      } else {
        renderFallback2DQuad(ctx, quad, state);
      }

      if (!options.hideHandles) {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(quad.tl.x, quad.tl.y);
        ctx.lineTo(quad.tr.x, quad.tr.y);
        ctx.lineTo(quad.br.x, quad.br.y);
        ctx.lineTo(quad.bl.x, quad.bl.y);
        ctx.closePath();
        ctx.strokeStyle = 'rgba(201, 168, 76, 0.9)';
        ctx.lineWidth = 2;
        ctx.setLineDash([7, 5]);
        ctx.stroke();
        ctx.setLineDash([]);

        const labels = [
          { key: 'tl', label: 'TL', pt: quad.tl },
          { key: 'tr', label: 'TR', pt: quad.tr },
          { key: 'br', label: 'BR', pt: quad.br },
          { key: 'bl', label: 'BL', pt: quad.bl }
        ];
        labels.forEach(({ label, pt: p }) => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 13, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(20, 20, 22, 0.92)';
          ctx.fill();
          ctx.lineWidth = 2.5;
          ctx.strokeStyle = '#C9A84C';
          ctx.stroke();

          ctx.fillStyle = '#FFFFFF';
          ctx.font = '700 10px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, p.x, p.y);
        });
        ctx.restore();
      }
    } else {
      // ======================================================================
      // MODE 1: FULL INTERACTIVE 3D THREE.JS ROOM STUDIO (Never a 2D pasted background)
      // ======================================================================
      if (webgl) {
        renderWebGLTreatmentScene(webgl, state, pricing, 'studio3d');
        ctx.drawImage(webgl.offscreen, 0, 0, W, H);
      } else {
        const quad = {
          tl: { x: W * 0.25, y: H * 0.15 },
          tr: { x: W * 0.75, y: H * 0.15 },
          br: { x: W * 0.75, y: H * 0.75 },
          bl: { x: W * 0.25, y: H * 0.75 }
        };
        renderFallback2DQuad(ctx, quad, state);
      }

      // Architectural dimension & window shape pill at top right (included in exported 1280x720 quote JPEG)
      if (options.hideHandles) {
        const dimDetail = state.windowPreset === 'bay-window'
          ? `Bay (${pricing.bayLeftMm}+${pricing.bayCenterMm}+${pricing.bayRightMm}mm) × ${pricing.dropMm}mm D`
          : state.windowPreset === 'corner-window'
            ? `90° Corner (${pricing.cornerLeftMm}+${pricing.cornerRightMm}mm) × ${pricing.dropMm}mm D`
            : `${pricing.widthMm}mm W × ${pricing.dropMm}mm D`;

        ctx.save();
        const badgeText = `${dimDetail}  •  ${pleat.name}`;
        ctx.font = '600 12px sans-serif';
        const tw = ctx.measureText(badgeText).width + 26;
        ctx.fillStyle = 'rgba(20, 20, 22, 0.78)';
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(W - tw - 18, 16, tw, 28, 14);
        } else {
          ctx.rect(W - tw - 18, 16, tw, 28);
        }
        ctx.fill();
        ctx.strokeStyle = 'rgba(201, 168, 76, 0.65)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = '#F5F2EB';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(badgeText, W - tw * 0.5 - 18, 30);
        ctx.restore();
      }
    }

    // Bottom Specification Summary Bar (included in exported 1280x720 quote JPEG)
    if (options.hideHandles) {
      const primaryFab = getFabricById(state.primaryFabricId);
      const primarySwatch = primaryFab.swatches.find(s => s.id === state.primarySwatchId) || primaryFab.swatches[0];

      ctx.save();
      ctx.fillStyle = 'rgba(18, 18, 20, 0.88)';
      ctx.fillRect(0, H - 54, W, 54);
      ctx.fillStyle = '#C9A84C';
      ctx.font = '700 13px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(
        `LEISURE LOUNGES CUSTOM CURTAINS & BLINDS  •  ${pleat.name.toUpperCase()} (${pricing.widthMm}mm W × ${pricing.dropMm}mm D)`,
        24,
        H - 31
      );
      ctx.fillStyle = '#EAE6DF';
      ctx.font = '400 12px sans-serif';
      ctx.fillText(
        `${primaryFab.supplier} ${primaryFab.name} (${primarySwatch.name})  |  ${pricing.track.description}`,
        24,
        H - 13
      );
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '700 16px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`Est. Supply: $${pricing.retailIncGst.toLocaleString('en-AU')} inc. GST`, W - 24, H - 22);
      ctx.restore();
    }
  }

  /**
   * Build frozen design object for HubSpot Quote & Shopify Cart (supports single or multi-window schedule)
   */
  function buildFrozenDesign(state, pricing, rootEl, windowSchedule = []) {
    const pleat = getPleatById(state.pleatId);
    const primaryFab = getFabricById(state.primaryFabricId);
    const primarySwatch = primaryFab.swatches.find(s => s.id === state.primarySwatchId) || primaryFab.swatches[0];
    const secondaryFab = getFabricById(state.secondaryFabricId || 'zepel-flo-ii');
    const secondarySwatch = secondaryFab.swatches.find(s => s.id === state.secondarySwatchId) || secondaryFab.swatches[0];
    const preset = WINDOW_PRESETS.find(w => w.id === state.windowPreset) || WINDOW_PRESETS[0];

    const sourceProduct =
      rootEl?.dataset?.sourceProduct ||
      'https://leisurelounges.com.au/products/custom-made-curtains';

    const effectiveLiningLabel = pleat.isRoman
      ? 'Integrated Blockout/Sateen Lining & Rear Battens'
      : pricing.primaryLayer.liningLabel || state.liningType.replace(/_/g, ' ');

    const secondarySummary = pleat.isRoman
      ? 'Integrated Blockout/Sateen Lining & Rear Battens'
      : state.layerMode === 'double'
        ? `Double Layer with ${secondaryFab.supplier} ${secondaryFab.name} (${secondarySwatch.name}) + ${effectiveLiningLabel} on primary`
        : `Single Layer (${effectiveLiningLabel})`;

    const sectionSpec = state.windowPreset === 'bay-window'
      ? ` [Left Wing ${pricing.bayLeftMm}mm + Centre Bay ${pricing.bayCenterMm}mm + Right Wing ${pricing.bayRightMm}mm]`
      : state.windowPreset === 'corner-window'
        ? ` [Left Return ${pricing.cornerLeftMm}mm + Right Wall ${pricing.cornerRightMm}mm @ 90°]`
        : '';

    const qtyPrefix = pricing.windowQty > 1 ? `${pricing.windowQty}× Identical Windows • ` : '';

    const activeItems = Array.isArray(windowSchedule) && windowSchedule.length > 0
      ? windowSchedule
      : null;

    const scheduleSummaryLines = activeItems
      ? activeItems.map((item, idx) =>
          `Window ${idx + 1} (${item.windowLabel}, Qty ${item.windowQty}): ${item.windowType} — ${item.dimensions} | ${item.headingStyle} | ${item.primaryFabric} | ${item.secondarySummary} | ${item.trackSummary} => $${item.retailIncGst.toLocaleString('en-AU')} inc. GST`
        )
      : [];

    const combinedOrderRetailIncGst = activeItems
      ? activeItems.reduce((sum, item) => sum + item.retailIncGst, 0)
      : pricing.retailIncGst;

    const totalWindowsCount = activeItems
      ? activeItems.reduce((sum, item) => sum + item.windowQty, 0)
      : pricing.windowQty;

    return {
      sourceProduct,
      windowLabel: state.windowLabel || 'Window 1 — Living Room',
      windowQty: pricing.windowQty,
      windowType: preset.name,
      dimensions: `${qtyPrefix}${pricing.widthMm}mm Total Width${sectionSpec} × ${pricing.dropMm}mm Drop`,
      headingStyle: pleat.isRoman
        ? `${pleat.name} (${pricing.primaryLayer.driveLabel})`
        : `${pleat.name} (${Math.round(pleat.fullness * 100)}% fullness, ${state.stacking})`,
      trackSummary: pricing.track.description,
      primaryFabric: `${primaryFab.supplier} ${primaryFab.name} — ${primarySwatch.name} (${primaryFab.rollWidthCm}cm roll)`,
      liningSpecification: effectiveLiningLabel,
      secondarySummary,
      supplyPriceSummary: activeItems
        ? `$${combinedOrderRetailIncGst.toLocaleString('en-AU')} AUD inc. GST across ${totalWindowsCount} window(s) in Multi-Window Schedule`
        : `$${pricing.retailIncGst.toLocaleString('en-AU')} AUD inc. GST (${pricing.windowQty > 1 ? `${pricing.windowQty}× @ $${pricing.unitRetailIncGst.toLocaleString('en-AU')} ea; ` : ''}Primary Make & Fabric: $${pricing.primaryRetailIncGst.toLocaleString('en-AU')}${pricing.liningRetailIncGst ? `, Lining: +$${pricing.liningRetailIncGst.toLocaleString('en-AU')}` : ''}${pricing.secondaryRetailIncGst ? `, Rear Sheer: $${pricing.secondaryRetailIncGst.toLocaleString('en-AU')}` : ''}${pricing.trackRetailIncGst ? `, Track: $${pricing.trackRetailIncGst.toLocaleString('en-AU')}` : ''}, Freight: $${pricing.freightRetailIncGst})`,
      installPreference: state.installPreference || 'Request check-measure & installation quote (Sydney / NSW)',
      visualiserMode: state.mode === 'ar' ? 'See on Your Window (3D PBR AR Perspective)' : `Interactive 3D Room Studio (${state.cameraView || 'room'})`,
      totalWindowsCount,
      combinedOrderRetailIncGst,
      scheduleSummaryLines,
      pricingBreakdown: {
        unitRetailIncGst: pricing.unitRetailIncGst,
        retailIncGst: pricing.retailIncGst,
        primaryRetailIncGst: pricing.primaryRetailIncGst,
        liningRetailIncGst: pricing.liningRetailIncGst,
        secondaryRetailIncGst: pricing.secondaryRetailIncGst,
        trackRetailIncGst: pricing.trackRetailIncGst,
        freightRetailIncGst: pricing.freightRetailIncGst
      }
    };
  }

  // Preset 4-corner window quads for the 3 AR sample room photographs
  const AR_SAMPLE_DEFAULT_CORNERS = {
    'living-room': {
      tl: { x: 0.252, y: 0.130 },
      tr: { x: 0.748, y: 0.130 },
      br: { x: 0.748, y: 0.724 },
      bl: { x: 0.252, y: 0.724 }
    },
    'bay-window-room': {
      tl: { x: 0.245, y: 0.195 },
      tr: { x: 0.755, y: 0.195 },
      br: { x: 0.755, y: 0.758 },
      bl: { x: 0.245, y: 0.758 }
    },
    'theatre-room': {
      tl: { x: 0.272, y: 0.202 },
      tr: { x: 0.728, y: 0.202 },
      br: { x: 0.728, y: 0.784 },
      bl: { x: 0.272, y: 0.784 }
    }
  };

  function cloneCorners(c) {
    return {
      tl: { x: c.tl.x, y: c.tl.y },
      tr: { x: c.tr.x, y: c.tr.y },
      br: { x: c.br.x, y: c.br.y },
      bl: { x: c.bl.x, y: c.bl.y }
    };
  }

  /**
   * Mount interactive UI onto #ll-curtain-studio-root
   */
  function mountStudio(rootEl) {
    if (!rootEl || rootEl.dataset.mounted === 'true') return;
    rootEl.dataset.mounted = 'true';

    const state = {
      mode: '3d', // '3d' | 'ar'
      cameraView: 'room', // 'room' | 'angle' | 'header' | 'weave'
      windowLabel: 'Window 1 — Living Room',
      windowQty: 1,
      windowPreset: rootEl.dataset.defaultWindowPreset || 'sliding-door',
      widthMm: Number(rootEl.dataset.defaultWidth) || 3200,
      dropMm: Number(rootEl.dataset.defaultDrop) || 2600,
      bayLeftMm: 950,
      bayCenterMm: 2000,
      bayRightMm: 950,
      cornerLeftMm: 1600,
      cornerRightMm: 2000,
      fitType: 'face',
      pleatId: rootEl.dataset.defaultPleat || 's-wave',
      stacking: 'centre-open',
      romanDrive: 'chain-easy-lift',
      layerMode: 'double', // 'single' | 'double'
      trackSeriesId: 'styletrack',
      trackFinishId: 'black',
      mounting: 'top',
      bendsCount: 0,
      primaryFabricId: 'zepel-retreat',
      primarySwatchId: 'alabaster',
      liningType: 'coated_3pass_blockout',
      savedNonSheerLining: 'coated_3pass_blockout',
      secondaryFabricId: 'zepel-graceful',
      secondarySwatchId: 'milk',
      secondaryPleatId: 's-wave',
      drawPercent: 78,
      lighting: 'day',
      installPreference: 'In-Home Check-Measure & Installation Quote (Sydney, Macarthur, Wollongong & Southern Highlands)',
      arSampleId: 'living-room',
      arSourceElement: null,
      arStream: null,
      arSampledBrightness: 1.0,
      arCorners: cloneCorners(AR_SAMPLE_DEFAULT_CORNERS['living-room']),
      windowSchedule: [],
      editingScheduleIndex: -1
    };

    const canvas = rootEl.querySelector('#ll-curtain-canvas');
    const modeBtns = rootEl.querySelectorAll('[data-curtain-mode]');
    const camBtns = rootEl.querySelectorAll('[data-camera-view]');
    const camBar = rootEl.querySelector('#ll-curtain-cam-bar');
    const stageBadge = rootEl.querySelector('#ll-curtain-stage-badge');
    const arToolbar = rootEl.querySelector('#ll-curtain-ar-toolbar');
    const presetBtns = rootEl.querySelectorAll('[data-window-preset]');
    const windowLabelInput = rootEl.querySelector('#ll-curtain-window-label');
    const windowQtyInput = rootEl.querySelector('#ll-curtain-window-qty');
    const widthInput = rootEl.querySelector('#ll-curtain-width');
    const widthRange = rootEl.querySelector('#ll-curtain-width-range');
    const dropInput = rootEl.querySelector('#ll-curtain-drop');
    const dropRange = rootEl.querySelector('#ll-curtain-drop-range');
    const bayDimsWrap = rootEl.querySelector('#ll-curtain-bay-dims-wrap');
    const bayLeftInput = rootEl.querySelector('#ll-curtain-bay-left');
    const bayCenterInput = rootEl.querySelector('#ll-curtain-bay-center');
    const bayRightInput = rootEl.querySelector('#ll-curtain-bay-right');
    const cornerDimsWrap = rootEl.querySelector('#ll-curtain-corner-dims-wrap');
    const cornerLeftInput = rootEl.querySelector('#ll-curtain-corner-left');
    const cornerRightInput = rootEl.querySelector('#ll-curtain-corner-right');
    const pleatGrid = rootEl.querySelector('#ll-curtain-pleat-grid');
    const layerBtns = rootEl.querySelectorAll('[data-layer-mode]');
    const trackSelect = rootEl.querySelector('#ll-curtain-track-series');
    const trackFinishGrid = rootEl.querySelector('#ll-curtain-track-finishes');
    const mountingSelect = rootEl.querySelector('#ll-curtain-mounting');
    const bendsSelect = rootEl.querySelector('#ll-curtain-bends');
    const romanDriveWrap = rootEl.querySelector('#ll-curtain-roman-drive-wrap');
    const trackConfigWrap = rootEl.querySelector('#ll-curtain-track-config-wrap');
    const romanDriveSelect = rootEl.querySelector('#ll-curtain-roman-drive');
    const stackingSelect = rootEl.querySelector('#ll-curtain-stacking');
    const liningSelect = rootEl.querySelector('#ll-curtain-lining');
    const liningNoteEl = rootEl.querySelector('#ll-curtain-lining-note');
    const primaryCatFilter = rootEl.querySelectorAll('[data-fabric-filter]');
    const primaryFabricSelect = rootEl.querySelector('#ll-curtain-primary-fabric');
    const primarySwatchesEl = rootEl.querySelector('#ll-curtain-primary-swatches');
    const secondaryWrap = rootEl.querySelector('#ll-curtain-secondary-wrap');
    const secondaryFabricSelect = rootEl.querySelector('#ll-curtain-secondary-fabric');
    const secondarySwatchesEl = rootEl.querySelector('#ll-curtain-secondary-swatches');
    const drawSlider = rootEl.querySelector('#ll-curtain-draw-slider');
    const lightingBtns = rootEl.querySelectorAll('[data-lighting-mode]');
    const installSelect = rootEl.querySelector('#ll-curtain-install-pref');

    // Summary outputs
    const totalPriceEl = rootEl.querySelector('#ll-curtain-total-price');
    const primaryPriceEl = rootEl.querySelector('#ll-curtain-primary-price');
    const liningPriceRow = rootEl.querySelector('#ll-curtain-lining-price-row');
    const liningPriceEl = rootEl.querySelector('#ll-curtain-lining-price');
    const secondaryPriceRow = rootEl.querySelector('#ll-curtain-secondary-price-row');
    const secondaryPriceEl = rootEl.querySelector('#ll-curtain-secondary-price');
    const trackPriceEl = rootEl.querySelector('#ll-curtain-track-price');
    const freightPriceEl = rootEl.querySelector('#ll-curtain-freight-price');
    const specSummaryEl = rootEl.querySelector('#ll-curtain-spec-summary');

    // Multi-Window Schedule elements
    const scheduleCountEl = rootEl.querySelector('#ll-curtain-schedule-count');
    const addWindowBtn = rootEl.querySelector('#ll-curtain-add-window-btn');
    const newWindowBtn = rootEl.querySelector('#ll-curtain-new-window-btn');
    const scheduleListEl = rootEl.querySelector('#ll-curtain-schedule-list');
    const scheduleTotalBar = rootEl.querySelector('#ll-curtain-schedule-total-bar');
    const scheduleTotalWindowsEl = rootEl.querySelector('#ll-curtain-schedule-total-windows');
    const scheduleGrandTotalEl = rootEl.querySelector('#ll-curtain-schedule-grand-total');

    let activeFabricCategory = 'all';

    function snapshotCurrentWindowItem() {
      const pricing = calculateCompletePricing(state);
      const frozen = buildFrozenDesign(state, pricing, rootEl, []);
      return {
        id: `win-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        windowLabel: state.windowLabel || `Window ${state.windowSchedule.length + 1}`,
        windowQty: pricing.windowQty,
        windowPreset: state.windowPreset,
        windowType: frozen.windowType,
        widthMm: pricing.widthMm,
        dropMm: pricing.dropMm,
        bayLeftMm: pricing.bayLeftMm,
        bayCenterMm: pricing.bayCenterMm,
        bayRightMm: pricing.bayRightMm,
        cornerLeftMm: pricing.cornerLeftMm,
        cornerRightMm: pricing.cornerRightMm,
        pleatId: state.pleatId,
        stacking: state.stacking,
        romanDrive: state.romanDrive,
        layerMode: state.layerMode,
        trackSeriesId: state.trackSeriesId,
        trackFinishId: state.trackFinishId,
        mounting: state.mounting,
        bendsCount: state.bendsCount,
        primaryFabricId: state.primaryFabricId,
        primarySwatchId: state.primarySwatchId,
        liningType: state.liningType,
        secondaryFabricId: state.secondaryFabricId,
        secondarySwatchId: state.secondarySwatchId,
        dimensions: frozen.dimensions,
        headingStyle: frozen.headingStyle,
        primaryFabric: frozen.primaryFabric,
        liningSpecification: frozen.liningSpecification,
        secondarySummary: frozen.secondarySummary,
        trackSummary: frozen.trackSummary,
        unitRetailIncGst: pricing.unitRetailIncGst,
        retailIncGst: pricing.retailIncGst
      };
    }

    function loadScheduleItemIntoState(item, index) {
      state.editingScheduleIndex = index;
      state.windowLabel = item.windowLabel;
      state.windowQty = item.windowQty || 1;
      state.windowPreset = item.windowPreset || 'sliding-door';
      state.widthMm = item.widthMm || 3200;
      state.dropMm = item.dropMm || 2600;
      state.bayLeftMm = item.bayLeftMm || 950;
      state.bayCenterMm = item.bayCenterMm || 2000;
      state.bayRightMm = item.bayRightMm || 950;
      state.cornerLeftMm = item.cornerLeftMm || 1600;
      state.cornerRightMm = item.cornerRightMm || 2000;
      state.pleatId = item.pleatId || 's-wave';
      state.stacking = item.stacking || 'centre-open';
      state.romanDrive = item.romanDrive || 'chain-easy-lift';
      state.layerMode = item.layerMode || 'double';
      state.trackSeriesId = item.trackSeriesId || 'styletrack';
      state.trackFinishId = item.trackFinishId || 'black';
      state.mounting = item.mounting || 'top';
      state.bendsCount = item.bendsCount || 0;
      state.primaryFabricId = item.primaryFabricId || 'zepel-retreat';
      state.primarySwatchId = item.primarySwatchId || 'alabaster';
      state.liningType = item.liningType || 'coated_3pass_blockout';
      if (state.liningType !== 'unlined') {
        state.savedNonSheerLining = state.liningType;
      }
      state.secondaryFabricId = item.secondaryFabricId || 'zepel-graceful';
      state.secondarySwatchId = item.secondarySwatchId || 'milk';

      if (windowLabelInput) windowLabelInput.value = state.windowLabel;
      if (windowQtyInput) windowQtyInput.value = String(state.windowQty);
      if (widthInput) widthInput.value = String(state.widthMm);
      if (widthRange) widthRange.value = String(Math.min(6000, state.widthMm));
      if (dropInput) dropInput.value = String(state.dropMm);
      if (dropRange) dropRange.value = String(state.dropMm);
      if (bayLeftInput) bayLeftInput.value = String(state.bayLeftMm);
      if (bayCenterInput) bayCenterInput.value = String(state.bayCenterMm);
      if (bayRightInput) bayRightInput.value = String(state.bayRightMm);
      if (cornerLeftInput) cornerLeftInput.value = String(state.cornerLeftMm);
      if (cornerRightInput) cornerRightInput.value = String(state.cornerRightMm);
      if (trackSelect) trackSelect.value = state.trackSeriesId;
      if (mountingSelect) mountingSelect.value = state.mounting;
      if (bendsSelect) bendsSelect.value = String(state.bendsCount);
      if (romanDriveSelect) romanDriveSelect.value = state.romanDrive;
      if (stackingSelect) stackingSelect.value = state.stacking;
      if (liningSelect) liningSelect.value = state.liningType;

      activeFabricCategory = 'all';
      primaryCatFilter.forEach(b => b.classList.toggle('is-active', b.dataset.fabricFilter === 'all'));
      renderPleatCards();
      renderTrackFinishes();
      populateFabricSelects();
      if (canvas.__llWebGLCtx) canvas.__llWebGLCtx.lastPresetCamera = null;
      updateAll();
    }

    function renderWindowScheduleUI() {
      const count = state.windowSchedule.length;
      const totalWindows = state.windowSchedule.reduce((acc, w) => acc + (w.windowQty || 1), 0);
      const grandTotal = state.windowSchedule.reduce((acc, w) => acc + (w.retailIncGst || 0), 0);

      if (scheduleCountEl) scheduleCountEl.textContent = String(totalWindows);
      if (addWindowBtn) {
        addWindowBtn.textContent = state.editingScheduleIndex >= 0
          ? `✓ Save Changes to Window #${state.editingScheduleIndex + 1}`
          : '+ Add This Window to Order Schedule';
      }
      if (newWindowBtn) {
        newWindowBtn.hidden = count === 0;
      }

      if (scheduleListEl) {
        if (count === 0) {
          scheduleListEl.innerHTML = '';
        } else {
          scheduleListEl.innerHTML = state.windowSchedule.map((item, idx) => `
            <div class="ll-curtain-schedule-item ${state.editingScheduleIndex === idx ? 'is-editing' : ''}">
              <div class="ll-curtain-schedule-item-meta">
                <strong style="color:#ffffff;font-size:1.25rem;">${idx + 1}. ${item.windowLabel} ${item.windowQty > 1 ? `(${item.windowQty}× Identical)` : ''}</strong>
                <div>${item.windowType} • ${item.widthMm}mm W × ${item.dropMm}mm D • ${item.headingStyle}</div>
                <div>${item.primaryFabric} • ${item.liningSpecification} • ${item.trackSummary}</div>
              </div>
              <div class="ll-curtain-schedule-item-controls">
                <strong style="color:#c9a84c;font-size:1.4rem;margin-right:0.5rem;">$${item.retailIncGst.toLocaleString('en-AU')}</strong>
                <button type="button" class="ll-curtain-mini-btn" data-sched-action="qty-minus" data-sched-idx="${idx}" title="Decrease Quantity">−</button>
                <span style="color:#fff;font-size:1.15rem;min-width:2.2rem;text-align:center;">${item.windowQty}×</span>
                <button type="button" class="ll-curtain-mini-btn" data-sched-action="qty-plus" data-sched-idx="${idx}" title="Increase Quantity">+</button>
                <button type="button" class="ll-curtain-mini-btn" data-sched-action="edit" data-sched-idx="${idx}">Preview / Edit</button>
                <button type="button" class="ll-curtain-mini-btn" data-sched-action="duplicate" data-sched-idx="${idx}">Duplicate</button>
                <button type="button" class="ll-curtain-mini-btn" data-sched-action="remove" data-sched-idx="${idx}">✕</button>
              </div>
            </div>
          `).join('');

          scheduleListEl.querySelectorAll('[data-sched-action]').forEach(btn => {
            btn.addEventListener('click', () => {
              const idx = Number(btn.dataset.schedIdx);
              const act = btn.dataset.schedAction;
              const item = state.windowSchedule[idx];
              if (!item) return;

              if (act === 'edit') {
                loadScheduleItemIntoState(item, idx);
              } else if (act === 'duplicate') {
                const copy = {
                  ...item,
                  id: `win-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                  windowLabel: `${item.windowLabel} (Copy)`
                };
                state.windowSchedule.push(copy);
                renderWindowScheduleUI();
              } else if (act === 'remove') {
                state.windowSchedule.splice(idx, 1);
                if (state.editingScheduleIndex === idx) {
                  state.editingScheduleIndex = -1;
                } else if (state.editingScheduleIndex > idx) {
                  state.editingScheduleIndex -= 1;
                }
                renderWindowScheduleUI();
              } else if (act === 'qty-plus' || act === 'qty-minus') {
                const nextQty = Math.max(1, Math.min(20, (item.windowQty || 1) + (act === 'qty-plus' ? 1 : -1)));
                item.windowQty = nextQty;
                item.retailIncGst = item.unitRetailIncGst * nextQty;
                if (state.editingScheduleIndex === idx) {
                  state.windowQty = nextQty;
                  if (windowQtyInput) windowQtyInput.value = String(nextQty);
                  updateAll();
                } else {
                  renderWindowScheduleUI();
                }
              }
            });
          });
        }
      }

      if (scheduleTotalBar) {
        scheduleTotalBar.hidden = count === 0;
      }
      if (scheduleTotalWindowsEl) {
        scheduleTotalWindowsEl.textContent = String(totalWindows);
      }
      if (scheduleGrandTotalEl) {
        scheduleGrandTotalEl.textContent = `$${grandTotal.toLocaleString('en-AU')}`;
      }
      if (buySupplyBtn) {
        buySupplyBtn.textContent = count > 0
          ? `Buy Supply-Only Now — Add All ${totalWindows} Windows ($${grandTotal.toLocaleString('en-AU')} inc. GST)`
          : `Buy Supply-Only Now (Add Custom Spec to Order)`;
      }
    }

    function renderPleatCards() {
      if (!pleatGrid) return;
      pleatGrid.innerHTML = PLEAT_STYLES.map(p => `
        <button type="button" class="ll-curtain-pleat-card ${state.pleatId === p.id ? 'is-active' : ''}" data-pleat-id="${p.id}">
          <span class="ll-curtain-pleat-badge">${p.badge}</span>
          <strong>${p.name}</strong>
          <small>${p.description}</small>
        </button>
      `).join('');
      pleatGrid.querySelectorAll('[data-pleat-id]').forEach(btn => {
        btn.addEventListener('click', () => {
          state.pleatId = btn.dataset.pleatId;
          if (state.pleatId === 'roman-blind') {
            state.layerMode = 'single';
          }
          renderPleatCards();
          updateAll();
        });
      });
    }

    function renderTrackFinishes() {
      if (!trackFinishGrid) return;
      const series = TRACK_TABLES[state.trackSeriesId] || TRACK_TABLES.styletrack;
      if (!series.finishes.some(f => f.id === state.trackFinishId)) {
        state.trackFinishId = series.finishes[0].id;
      }
      trackFinishGrid.innerHTML = series.finishes.map(f => `
        <button type="button" class="ll-curtain-swatch-chip ${state.trackFinishId === f.id ? 'is-active' : ''}" data-track-finish="${f.id}" title="${f.name}">
          <span class="ll-curtain-swatch-dot" style="background:${f.hex}"></span>
          <span>${f.name}</span>
        </button>
      `).join('');
      trackFinishGrid.querySelectorAll('[data-track-finish]').forEach(btn => {
        btn.addEventListener('click', () => {
          state.trackFinishId = btn.dataset.trackFinish;
          renderTrackFinishes();
          updateAll();
        });
      });
    }

    function populateFabricSelects() {
      if (!primaryFabricSelect) return;
      const filtered = activeFabricCategory === 'all'
        ? FABRIC_RANGES
        : FABRIC_RANGES.filter(f => f.category === activeFabricCategory);

      if (!filtered.some(f => f.id === state.primaryFabricId)) {
        state.primaryFabricId = filtered[0].id;
      }

      primaryFabricSelect.innerHTML = filtered.map(f => `
        <option value="${f.id}" ${f.id === state.primaryFabricId ? 'selected' : ''}>
          ${f.supplier} — ${f.name} (${f.rollWidthCm}cm, ${f.category.toUpperCase()})
        </option>
      `).join('');

      if (secondaryFabricSelect) {
        const sheers = FABRIC_RANGES.filter(f => f.category === 'sheer');
        secondaryFabricSelect.innerHTML = sheers.map(f => `
          <option value="${f.id}" ${f.id === state.secondaryFabricId ? 'selected' : ''}>
            ${f.supplier} — ${f.name} (${f.rollWidthCm}cm Sheer)
          </option>
        `).join('');
      }
      renderPrimarySwatches();
      renderSecondarySwatches();
    }

    function renderPrimarySwatches() {
      if (!primarySwatchesEl) return;
      const fab = getFabricById(state.primaryFabricId);
      if (!fab.swatches.some(s => s.id === state.primarySwatchId)) {
        state.primarySwatchId = fab.swatches[0].id;
      }
      primarySwatchesEl.innerHTML = fab.swatches.map(s => `
        <button type="button" class="ll-curtain-swatch-chip ${state.primarySwatchId === s.id ? 'is-active' : ''}" data-primary-swatch="${s.id}">
          <span class="ll-curtain-swatch-dot" style="background:${s.hex}"></span>
          <span>${s.name}</span>
        </button>
      `).join('');
      primarySwatchesEl.querySelectorAll('[data-primary-swatch]').forEach(btn => {
        btn.addEventListener('click', () => {
          state.primarySwatchId = btn.dataset.primarySwatch;
          renderPrimarySwatches();
          updateAll();
        });
      });
    }

    function renderSecondarySwatches() {
      if (!secondarySwatchesEl) return;
      const fab = getFabricById(state.secondaryFabricId);
      if (!fab.swatches.some(s => s.id === state.secondarySwatchId)) {
        state.secondarySwatchId = fab.swatches[0].id;
      }
      secondarySwatchesEl.innerHTML = fab.swatches.map(s => `
        <button type="button" class="ll-curtain-swatch-chip ${state.secondarySwatchId === s.id ? 'is-active' : ''}" data-secondary-swatch="${s.id}">
          <span class="ll-curtain-swatch-dot" style="background:${s.hex}"></span>
          <span>${s.name}</span>
        </button>
      `).join('');
      secondarySwatchesEl.querySelectorAll('[data-secondary-swatch]').forEach(btn => {
        btn.addEventListener('click', () => {
          state.secondarySwatchId = btn.dataset.secondarySwatch;
          renderSecondarySwatches();
          updateAll();
        });
      });
    }

    function updateAll() {
      const pleat = getPleatById(state.pleatId);
      const primaryFab = getFabricById(state.primaryFabricId);

      if (romanDriveWrap) romanDriveWrap.hidden = !pleat.isRoman;
      if (trackConfigWrap) trackConfigWrap.hidden = pleat.isRoman;
      if (secondaryWrap) secondaryWrap.hidden = pleat.isRoman || state.layerMode !== 'double';

      // Show/hide 3-Sided Bay & 90° Corner individual section width inputs
      const isBay = state.windowPreset === 'bay-window';
      const isCorner = state.windowPreset === 'corner-window';
      if (bayDimsWrap) bayDimsWrap.hidden = !isBay;
      if (cornerDimsWrap) cornerDimsWrap.hidden = !isCorner;

      if (isBay) {
        state.widthMm = state.bayLeftMm + state.bayCenterMm + state.bayRightMm;
        state.bendsCount = 2;
        if (widthInput) widthInput.value = String(state.widthMm);
        if (widthRange) widthRange.value = String(Math.min(6000, state.widthMm));
        if (state.stacking !== 'centre-open') {
          state.savedNonBayStacking = state.stacking;
          state.stacking = 'centre-open';
        }
      } else {
        if (state.savedNonBayStacking) {
          state.stacking = state.savedNonBayStacking;
          state.savedNonBayStacking = null;
        }
        if (isCorner) {
          state.widthMm = state.cornerLeftMm + state.cornerRightMm;
          state.bendsCount = 1;
          if (widthInput) widthInput.value = String(state.widthMm);
          if (widthRange) widthRange.value = String(Math.min(6000, state.widthMm));
        } else {
          state.bendsCount = 0;
        }
      }
      if (bendsSelect) {
        bendsSelect.value = String(state.bendsCount);
        bendsSelect.disabled = true;
      }

      // 3-Sided Bay Window uses Centre Opening Only (2 halves opening to their own outer ends)
      if (stackingSelect) {
        const hasSingleStackOpt = Boolean(stackingSelect.querySelector('option[value="left-stack"]'));
        if (isBay && hasSingleStackOpt) {
          stackingSelect.innerHTML = [
            '<option value="centre-open">Centre Opening Pair (2 Halves Open to Outer Ends)</option>'
          ].join('');
        } else if (!isBay && !hasSingleStackOpt) {
          stackingSelect.innerHTML = [
            '<option value="centre-open">Centre Opening Pair (Left + Right Stack)</option>',
            '<option value="left-stack">Single Panel — Stack to Left</option>',
            '<option value="right-stack">Single Panel — Stack to Right</option>'
          ].join('');
        }
        stackingSelect.value = state.stacking;
      }

      // Enforce Sheer Lining Lock & update Lining helper note
      if (liningSelect) {
        if (pleat.isRoman) {
          liningSelect.disabled = true;
          if (liningNoteEl) {
            liningNoteEl.textContent = 'Roman Blinds include full rear-battened blockout/sateen lining in the headrail grid price.';
          }
        } else if (primaryFab.category === 'sheer') {
          if (state.liningType !== 'unlined') {
            state.savedNonSheerLining = state.liningType;
          }
          state.liningType = 'unlined';
          liningSelect.value = 'unlined';
          liningSelect.disabled = true;
          if (liningNoteEl) {
            liningNoteEl.textContent = 'Sheer fabrics are manufactured unlined for natural daylight filtering. Select a Drapery, Blockout, or Velvet fabric to add Sateen or 3-Pass Blockout lining.';
          }
        } else {
          liningSelect.disabled = false;
          liningSelect.value = state.liningType;
          if (liningNoteEl) {
            liningNoteEl.textContent = 'Select 3-Pass Thermal & Acoustic Blockout for full room darkening and insulation, Polycotton Sateen for soft UV drape protection, or Unlined for natural light filtering.';
          }
        }
      }

      layerBtns.forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.layerMode === state.layerMode);
      });
      modeBtns.forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.curtainMode === state.mode);
      });
      camBtns.forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.cameraView === state.cameraView);
      });
      presetBtns.forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.windowPreset === state.windowPreset);
      });
      if (arToolbar) arToolbar.hidden = state.mode !== 'ar';
      if (camBar) camBar.hidden = state.mode === 'ar';

      const pricing = calculateCompletePricing(state);

      if (stageBadge) {
        if (state.mode === 'ar') {
          stageBadge.textContent = '3D PBR Window AR • Drag TL, TR, BR, BL corners onto your window';
        } else {
          const presetObj = WINDOW_PRESETS.find(w => w.id === state.windowPreset) || WINDOW_PRESETS[0];
          stageBadge.textContent = `${presetObj.name} (${pricing.widthMm}mm W × ${pricing.dropMm}mm D) • Drag to orbit · Scroll/pinch to zoom`;
        }
      }

      if (totalPriceEl) {
        totalPriceEl.textContent = pricing.windowQty > 1
          ? `$${pricing.retailIncGst.toLocaleString('en-AU')} (${pricing.windowQty}×)`
          : `$${pricing.retailIncGst.toLocaleString('en-AU')}`;
      }
      if (primaryPriceEl) {
        const extraLabel = pricing.isRoman && pricing.primaryLayer.blindsCount > 1
          ? `${pricing.primaryLayer.blindsCount} blinds, ${pricing.primaryLayer.fabricMetres}m fabric`
          : `${pricing.primaryLayer.fabricMetres}m fabric`;
        primaryPriceEl.textContent = `$${pricing.primaryRetailIncGst.toLocaleString('en-AU')} (${extraLabel})`;
      }
      if (liningPriceRow) {
        liningPriceRow.hidden = false;
      }
      if (liningPriceEl) {
        liningPriceEl.textContent = pricing.liningSummaryText || 'Included';
      }
      if (secondaryPriceRow) {
        secondaryPriceRow.hidden = !pricing.secondaryLayer;
      }
      if (secondaryPriceEl && pricing.secondaryLayer) {
        secondaryPriceEl.textContent = `$${pricing.secondaryRetailIncGst.toLocaleString('en-AU')} (${pricing.secondaryLayer.fabricMetres}m sheer)`;
      }
      if (trackPriceEl) {
        trackPriceEl.textContent = pricing.isRoman
          ? 'Included in Roman Headrail'
          : `$${pricing.trackRetailIncGst.toLocaleString('en-AU')}`;
      }
      if (freightPriceEl) {
        freightPriceEl.textContent = `$${pricing.freightRetailIncGst}`;
      }

      const frozen = buildFrozenDesign(state, pricing, rootEl, state.windowSchedule);
      if (specSummaryEl) {
        specSummaryEl.textContent = `${state.windowLabel} • ${frozen.headingStyle} • ${frozen.primaryFabric} • ${frozen.liningSpecification}`;
      }

      renderWindowScheduleUI();
      renderStudioCanvas(canvas, state, pricing);
    }

    // Event Listeners
    modeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        state.mode = btn.dataset.curtainMode;
        updateAll();
      });
    });

    camBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        state.cameraView = btn.dataset.cameraView;
        state.mode = '3d';
        if (canvas.__llWebGLCtx) {
          canvas.__llWebGLCtx.lastPresetCamera = null;
        }
        updateAll();
      });
    });

    presetBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const preset = WINDOW_PRESETS.find(w => w.id === btn.dataset.windowPreset);
        if (!preset) return;
        const prevPreset = state.windowPreset;
        state.windowPreset = preset.id;
        state.widthMm = preset.defaultW;
        state.dropMm = preset.defaultD;
        state.bendsCount = preset.defaultBends;
        if (preset.id === 'bay-window') {
          state.drawPercent = 28;
        } else if (prevPreset === 'bay-window' && state.drawPercent < 45) {
          state.drawPercent = 78;
        }
        if (drawSlider) drawSlider.value = String(state.drawPercent);
        if (preset.defaultBayLeft) {
          state.bayLeftMm = preset.defaultBayLeft;
          state.bayCenterMm = preset.defaultBayCenter;
          state.bayRightMm = preset.defaultBayRight;
          if (bayLeftInput) bayLeftInput.value = String(state.bayLeftMm);
          if (bayCenterInput) bayCenterInput.value = String(state.bayCenterMm);
          if (bayRightInput) bayRightInput.value = String(state.bayRightMm);
        }
        if (preset.defaultCornerLeft) {
          state.cornerLeftMm = preset.defaultCornerLeft;
          state.cornerRightMm = preset.defaultCornerRight;
          if (cornerLeftInput) cornerLeftInput.value = String(state.cornerLeftMm);
          if (cornerRightInput) cornerRightInput.value = String(state.cornerRightMm);
        }
        if (widthInput) widthInput.value = String(state.widthMm);
        if (widthRange) widthRange.value = String(Math.min(6000, state.widthMm));
        if (dropInput) dropInput.value = String(state.dropMm);
        if (dropRange) dropRange.value = String(state.dropMm);
        if (bendsSelect) bendsSelect.value = String(state.bendsCount);
        if (canvas.__llWebGLCtx) {
          canvas.__llWebGLCtx.lastPresetCamera = null;
        }
        updateAll();
      });
    });

    windowLabelInput?.addEventListener('input', e => {
      state.windowLabel = e.target.value || 'Window 1';
      updateAll();
    });

    windowQtyInput?.addEventListener('change', e => {
      state.windowQty = Math.max(1, Math.min(20, Math.round(Number(e.target.value) || 1)));
      windowQtyInput.value = String(state.windowQty);
      updateAll();
    });

    function syncWidth(val) {
      const targetW = Math.max(600, Math.min(12000, Number(val) || 2400));
      if (state.windowPreset === 'bay-window') {
        const curSum = Math.max(1000, state.bayLeftMm + state.bayCenterMm + state.bayRightMm);
        const ratio = targetW / curSum;
        state.bayLeftMm = Math.max(300, Math.round((state.bayLeftMm * ratio) / 50) * 50);
        state.bayRightMm = Math.max(300, Math.round((state.bayRightMm * ratio) / 50) * 50);
        state.bayCenterMm = Math.max(400, targetW - state.bayLeftMm - state.bayRightMm);
        if (bayLeftInput) bayLeftInput.value = String(state.bayLeftMm);
        if (bayCenterInput) bayCenterInput.value = String(state.bayCenterMm);
        if (bayRightInput) bayRightInput.value = String(state.bayRightMm);
      } else if (state.windowPreset === 'corner-window') {
        const curSum = Math.max(600, state.cornerLeftMm + state.cornerRightMm);
        const ratio = targetW / curSum;
        state.cornerLeftMm = Math.max(300, Math.round((state.cornerLeftMm * ratio) / 50) * 50);
        state.cornerRightMm = Math.max(300, targetW - state.cornerLeftMm);
        if (cornerLeftInput) cornerLeftInput.value = String(state.cornerLeftMm);
        if (cornerRightInput) cornerRightInput.value = String(state.cornerRightMm);
      }
      state.widthMm = targetW;
      if (widthInput) widthInput.value = String(state.widthMm);
      if (widthRange) widthRange.value = String(Math.min(6000, state.widthMm));
      updateAll();
    }

    function syncDrop(val) {
      state.dropMm = Math.max(600, Math.min(4200, Number(val) || 2400));
      if (dropInput) dropInput.value = String(state.dropMm);
      if (dropRange) dropRange.value = String(state.dropMm);
      updateAll();
    }

    widthInput?.addEventListener('change', e => syncWidth(e.target.value));
    widthRange?.addEventListener('input', e => syncWidth(e.target.value));
    dropInput?.addEventListener('change', e => syncDrop(e.target.value));
    dropRange?.addEventListener('input', e => syncDrop(e.target.value));

    [bayLeftInput, bayCenterInput, bayRightInput].forEach(inp => {
      inp?.addEventListener('input', () => {
        state.bayLeftMm = Math.max(300, Math.min(4000, Number(bayLeftInput?.value) || 950));
        state.bayCenterMm = Math.max(400, Math.min(6000, Number(bayCenterInput?.value) || 2000));
        state.bayRightMm = Math.max(300, Math.min(4000, Number(bayRightInput?.value) || 950));
        updateAll();
      });
    });

    [cornerLeftInput, cornerRightInput].forEach(inp => {
      inp?.addEventListener('input', () => {
        state.cornerLeftMm = Math.max(300, Math.min(6000, Number(cornerLeftInput?.value) || 1600));
        state.cornerRightMm = Math.max(300, Math.min(6000, Number(cornerRightInput?.value) || 2000));
        updateAll();
      });
    });

    // Multi-Window Schedule Add / Save / New Window Handlers
    addWindowBtn?.addEventListener('click', () => {
      const snap = snapshotCurrentWindowItem();
      if (state.editingScheduleIndex >= 0 && state.windowSchedule[state.editingScheduleIndex]) {
        state.windowSchedule[state.editingScheduleIndex] = snap;
        state.editingScheduleIndex = -1;
      } else {
        state.windowSchedule.push(snap);
      }
      const nextNum = state.windowSchedule.length + 1;
      state.windowLabel = `Window ${nextNum} — Room ${nextNum}`;
      state.windowQty = 1;
      if (windowLabelInput) windowLabelInput.value = state.windowLabel;
      if (windowQtyInput) windowQtyInput.value = '1';
      updateAll();
    });

    newWindowBtn?.addEventListener('click', () => {
      state.editingScheduleIndex = -1;
      const nextNum = state.windowSchedule.length + 1;
      state.windowLabel = `Window ${nextNum} — Room ${nextNum}`;
      state.windowQty = 1;
      if (windowLabelInput) windowLabelInput.value = state.windowLabel;
      if (windowQtyInput) windowQtyInput.value = '1';
      updateAll();
    });

    layerBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        state.layerMode = btn.dataset.layerMode;
        updateAll();
      });
    });

    trackSelect?.addEventListener('change', e => {
      state.trackSeriesId = e.target.value;
      renderTrackFinishes();
      updateAll();
    });

    mountingSelect?.addEventListener('change', e => {
      state.mounting = e.target.value;
      updateAll();
    });

    bendsSelect?.addEventListener('change', e => {
      state.bendsCount = Number(e.target.value) || 0;
      updateAll();
    });

    romanDriveSelect?.addEventListener('change', e => {
      state.romanDrive = e.target.value;
      updateAll();
    });

    stackingSelect?.addEventListener('change', e => {
      state.stacking = e.target.value;
      updateAll();
    });

    liningSelect?.addEventListener('change', e => {
      state.liningType = e.target.value;
      if (state.liningType !== 'unlined') {
        state.savedNonSheerLining = state.liningType;
      }
      updateAll();
    });

    primaryCatFilter.forEach(btn => {
      btn.addEventListener('click', () => {
        activeFabricCategory = btn.dataset.fabricFilter;
        primaryCatFilter.forEach(b => b.classList.toggle('is-active', b === btn));
        populateFabricSelects();
        const newFab = getFabricById(state.primaryFabricId);
        if (newFab.category !== 'sheer' && state.liningType === 'unlined' && state.savedNonSheerLining) {
          state.liningType = state.savedNonSheerLining;
        }
        updateAll();
      });
    });

    primaryFabricSelect?.addEventListener('change', e => {
      state.primaryFabricId = e.target.value;
      const newFab = getFabricById(state.primaryFabricId);
      if (newFab.category !== 'sheer' && state.liningType === 'unlined' && state.savedNonSheerLining) {
        state.liningType = state.savedNonSheerLining;
      }
      renderPrimarySwatches();
      updateAll();
    });

    secondaryFabricSelect?.addEventListener('change', e => {
      state.secondaryFabricId = e.target.value;
      renderSecondarySwatches();
      updateAll();
    });

    drawSlider?.addEventListener('input', e => {
      state.drawPercent = Number(e.target.value);
      updateAll();
    });

    lightingBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        state.lighting = btn.dataset.lightingMode;
        lightingBtns.forEach(b => b.classList.toggle('is-active', b === btn));
        updateAll();
      });
    });

    installSelect?.addEventListener('change', e => {
      state.installPreference = e.target.value;
      updateAll();
    });

    // AR Sample Room / Photo Upload / Live Camera Controls
    rootEl.querySelectorAll('[data-ar-sample]').forEach(btn => {
      btn.addEventListener('click', () => {
        state.arSampleId = btn.dataset.arSample;
        state.arSourceElement = null;
        state.mode = 'ar';
        const defaultC = AR_SAMPLE_DEFAULT_CORNERS[state.arSampleId] || AR_SAMPLE_DEFAULT_CORNERS['living-room'];
        state.arCorners = cloneCorners(defaultC);
        updateAll();
      });
    });

    const photoInput = rootEl.querySelector('#ll-curtain-photo-upload');
    photoInput?.addEventListener('change', e => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = ev => {
        const img = new Image();
        img.onload = () => {
          state.arSourceElement = img;
          state.mode = 'ar';
          updateAll();
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });

    const cameraBtn = rootEl.querySelector('#ll-curtain-camera-btn');
    cameraBtn?.addEventListener('click', async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        alert('Camera access is not available in this browser. Please use "Upload Window Photo" instead.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
        });
        const video = document.createElement('video');
        video.srcObject = stream;
        video.playsInline = true;
        await video.play();
        state.arSourceElement = video;
        state.arStream = stream;
        state.mode = 'ar';
        const pump = () => {
          if (state.arSourceElement === video && state.mode === 'ar') {
            updateAll();
            requestAnimationFrame(pump);
          }
        };
        requestAnimationFrame(pump);
      } catch {
        alert('Could not access camera. You can upload a window photo or use a sample room.');
      }
    });

    const resetQuadBtn = rootEl.querySelector('#ll-curtain-reset-quad');
    resetQuadBtn?.addEventListener('click', () => {
      const defaultC = AR_SAMPLE_DEFAULT_CORNERS[state.arSampleId] || AR_SAMPLE_DEFAULT_CORNERS['living-room'];
      state.arCorners = cloneCorners(defaultC);
      updateAll();
    });

    // Draggable 4-Corner AR Perspective Handles on Canvas + 3D OrbitControls Listener
    let draggingCorner = null;
    function getCanvasNormCoords(evt) {
      const rect = canvas.getBoundingClientRect();
      const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
      const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
      return {
        x: Math.max(0.02, Math.min(0.98, (clientX - rect.left) / rect.width)),
        y: Math.max(0.02, Math.min(0.94, (clientY - rect.top) / rect.height))
      };
    }

    canvas?.addEventListener('pointerdown', evt => {
      if (state.mode !== 'ar') return;
      const pos = getCanvasNormCoords(evt);
      let closestKey = null;
      let minDist = 0.065;
      for (const [k, pt] of Object.entries(state.arCorners)) {
        const dist = Math.hypot(pos.x - pt.x, pos.y - pt.y);
        if (dist < minDist) {
          minDist = dist;
          closestKey = k;
        }
      }
      if (closestKey) {
        draggingCorner = closestKey;
        canvas.setPointerCapture?.(evt.pointerId);
      }
    });

    canvas?.addEventListener('pointermove', evt => {
      if (!draggingCorner || state.mode !== 'ar') return;
      const pos = getCanvasNormCoords(evt);
      state.arCorners[draggingCorner] = pos;
      updateAll();
    });

    canvas?.addEventListener('pointerup', () => {
      draggingCorner = null;
    });

    // Attach OrbitControls change listener for ALL 3D views (including default 3D Room View)
    const webglInit = getOrInitWebGLContext(canvas, rootEl);
    if (webglInit?.controls) {
      webglInit.controls.addEventListener('change', () => {
        if (state.mode === '3d') {
          const pricing = calculateCompletePricing(state);
          renderStudioCanvas(canvas, state, pricing);
        }
      });
    }

    // Modal & Quote Submission
    const quoteModal = rootEl.querySelector('#ll-curtain-quote-modal');
    const openQuoteBtn = rootEl.querySelector('#ll-curtain-open-quote');
    const closeQuoteBtns = rootEl.querySelectorAll('[data-close-curtain-modal]');
    const quoteImgPreview = rootEl.querySelector('#ll-curtain-quote-preview');
    const quoteRefEl = rootEl.querySelector('#ll-curtain-quote-ref');
    const quoteForm = rootEl.querySelector('#ll-curtain-quote-form');
    const quoteStatus = rootEl.querySelector('#ll-curtain-quote-status');
    const downloadBtn = rootEl.querySelector('#ll-curtain-download-jpg');
    const buySupplyBtn = rootEl.querySelector('#ll-curtain-buy-supply');
    const cartStatusEl = rootEl.querySelector('#ll-curtain-cart-status');

    let activeQuoteSession = null;

    function captureClean1280x720Jpeg() {
      const pricing = calculateCompletePricing(state);
      renderStudioCanvas(canvas, state, pricing, { hideHandles: true });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      renderStudioCanvas(canvas, state, pricing, { hideHandles: false });
      return {
        dataUrl,
        pricing,
        design: buildFrozenDesign(state, pricing, rootEl, state.windowSchedule)
      };
    }

    openQuoteBtn?.addEventListener('click', () => {
      const { dataUrl, design } = captureClean1280x720Jpeg();
      const randHex = Array.from({ length: 12 }, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase();
      const reference = `CT-${randHex}`;
      activeQuoteSession = { reference, image: dataUrl, design };
      if (quoteImgPreview) quoteImgPreview.src = dataUrl;
      if (quoteRefEl) {
        quoteRefEl.textContent = `Design Reference: ${reference} (${design.totalWindowsCount} Window${design.totalWindowsCount > 1 ? 's' : ''} • Est. $${design.combinedOrderRetailIncGst.toLocaleString('en-AU')} inc. GST)`;
      }
      if (quoteStatus) quoteStatus.textContent = '';
      if (quoteModal) quoteModal.hidden = false;
    });

    closeQuoteBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (quoteModal) quoteModal.hidden = true;
      });
    });

    downloadBtn?.addEventListener('click', () => {
      const imgData = activeQuoteSession?.image || captureClean1280x720Jpeg().dataUrl;
      const ref = activeQuoteSession?.reference || 'CT-CUSTOM';
      const a = document.createElement('a');
      a.href = imgData;
      a.download = `${ref}-window-design.jpg`;
      a.click();
    });

    quoteForm?.addEventListener('submit', async evt => {
      evt.preventDefault();
      if (!activeQuoteSession) return;
      const formData = new FormData(quoteForm);
      const firstname = String(formData.get('firstname') || '').trim();
      const lastname = String(formData.get('lastname') || '').trim();
      const email = String(formData.get('email') || '').trim();
      const phone = String(formData.get('phone') || '').trim() || '(00) 0000 0000';
      const zip = String(formData.get('zip') || '').trim();
      const notes = String(formData.get('message') || '').trim() || 'None';

      if (!firstname || !lastname || !email || !/^\d{4}$/.test(zip)) {
        if (quoteStatus) quoteStatus.textContent = 'Please complete your name, email, and 4-digit Australian postcode.';
        return;
      }

      const d = activeQuoteSession.design;
      const ref = activeQuoteSession.reference;
      const scheduleBlock = d.scheduleSummaryLines && d.scheduleSummaryLines.length > 0
        ? ['Multi-Window Order Schedule:', ...d.scheduleSummaryLines, '']
        : [];

      const message = [
        'CUSTOM CURTAINS & ROMAN BLINDS — DESIGN QUOTE',
        `Reference: ${ref}`,
        `Source product: ${d.sourceProduct}`,
        `Total Windows in Quote: ${d.totalWindowsCount}`,
        `Window type: ${d.windowType}`,
        `Dimensions: ${d.dimensions}`,
        `Heading / Treatment: ${d.headingStyle}`,
        `Track system: ${d.trackSummary}`,
        `Primary fabric: ${d.primaryFabric}`,
        `Lining / Secondary layer: ${d.secondarySummary}`,
        `Estimated Supply Price (inc. GST): ${d.supplyPriceSummary}`,
        `Installation preference: ${d.installPreference}`,
        `Visualiser mode: ${d.visualiserMode}`,
        '',
        ...scheduleBlock,
        'Customer notes:',
        notes,
        '',
        'Full frozen specification:',
        JSON.stringify(d, null, 2),
        '',
        'This is a request for a custom curtain/blind quote or measure, not a final installation contract. Estimated supply price includes 10% GST; professional measure and installation is quoted separately per window inspection.'
      ].join('\n');

      const payload = {
        fields: [
          { objectTypeId: '0-1', name: 'firstname', value: firstname },
          { objectTypeId: '0-1', name: 'lastname', value: lastname },
          { objectTypeId: '0-1', name: 'email', value: email },
          { objectTypeId: '0-1', name: 'phone', value: phone },
          { objectTypeId: '0-1', name: 'zip', value: zip },
          { objectTypeId: '0-1', name: 'message', value: message }
        ],
        context: {
          pageName: `Custom Curtains & Roman Blinds — ${d.headingStyle} design quote`,
          pageUri: d.sourceProduct
        }
      };

      const submitBtn = quoteForm.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      if (quoteStatus) quoteStatus.textContent = 'Sending your window design & specification…';

      try {
        const uploadEndpoint = rootEl.dataset.quoteUploadEndpoint;
        if (uploadEndpoint) {
          const bytes = Uint8Array.from(atob(activeQuoteSession.image.split(',')[1]), c => c.charCodeAt(0));
          const blob = new Blob([bytes], { type: 'image/jpeg' });
          const fd = new FormData();
          fd.set('payload', JSON.stringify(payload));
          fd.set('reference', ref);
          fd.set('layoutImage', blob, `${ref}-layout.jpg`);
          const res = await fetch(uploadEndpoint, { method: 'POST', body: fd });
          if (!res.ok) throw new Error('Upload service rejected');
        } else {
          const res = await fetch(QUOTE_FORM_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          if (!res.ok) throw new Error('HubSpot form error');
        }
        if (quoteStatus) {
          quoteStatus.textContent = `Thank you! Your design (${ref}) has been sent to our curtain & interiors team.`;
        }
        try { window.llOpenAIAds?.trackLead(ref); } catch {}
      } catch {
        if (quoteStatus) {
          quoteStatus.textContent = `Saved locally under reference ${ref}. Download your design image below or call (02) 4648 2229.`;
        }
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });

    // Secondary CTA: Buy Supply-Only Now (Adds all windows in Multi-Window Schedule or current window to Shopify Cart)
    buySupplyBtn?.addEventListener('click', async () => {
      const { pricing, design } = captureClean1280x720Jpeg();
      const variantId = rootEl.dataset.variantId;
      const randHex = Array.from({ length: 8 }, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase();
      const reference = `CT-${randHex}`;

      const windowsToOrder = state.windowSchedule.length > 0
        ? state.windowSchedule
        : [snapshotCurrentWindowItem()];

      if (!variantId) {
        if (cartStatusEl) {
          cartStatusEl.hidden = false;
          cartStatusEl.textContent = `Supply-Only Order Spec Ready (${reference} • ${design.totalWindowsCount} window(s)): $${design.combinedOrderRetailIncGst.toLocaleString('en-AU')} inc. GST. Opening quote/order confirmation…`;
        }
        openQuoteBtn?.click();
        return;
      }

      const cartItems = windowsToOrder.map((win, idx) => ({
        id: Number(variantId),
        quantity: win.windowQty || 1,
        properties: {
          _Reference: `${reference}-W${idx + 1}`,
          'Window Label': win.windowLabel,
          'Window Architecture': win.windowType,
          Dimensions: win.dimensions,
          'Heading / Style': win.headingStyle,
          'Track System': win.trackSummary,
          'Primary Fabric': win.primaryFabric,
          'Lining Specification': win.liningSpecification,
          'Rear Layer': win.secondarySummary,
          'Unit Supply Price (inc. GST)': `$${win.unitRetailIncGst.toLocaleString('en-AU')} AUD`,
          'Line Subtotal (inc. GST)': `$${win.retailIncGst.toLocaleString('en-AU')} AUD`
        }
      }));

      try {
        buySupplyBtn.disabled = true;
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        });
        if (res.ok) {
          window.location.href = '/cart';
        } else {
          openQuoteBtn?.click();
        }
      } catch {
        openQuoteBtn?.click();
      } finally {
        buySupplyBtn.disabled = false;
      }
    });

    // Apply optional URL deep-link query params
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mode') === 'ar' || params.get('mode') === '3d') state.mode = params.get('mode');
      if (params.get('cam')) state.cameraView = params.get('cam');
      if (params.get('pleat')) state.pleatId = params.get('pleat');
      if (params.get('preset')) {
        const preset = WINDOW_PRESETS.find(w => w.id === params.get('preset'));
        if (preset) {
          state.windowPreset = preset.id;
          state.widthMm = preset.defaultW;
          state.dropMm = preset.defaultD;
          state.bendsCount = preset.defaultBends;
          if (preset.id === 'bay-window') {
            state.drawPercent = 28;
          }
          if (widthInput) widthInput.value = String(state.widthMm);
          if (widthRange) widthRange.value = String(Math.min(6000, state.widthMm));
          if (dropInput) dropInput.value = String(state.dropMm);
          if (dropRange) dropRange.value = String(state.dropMm);
          if (preset.defaultBayLeft) {
            state.bayLeftMm = preset.defaultBayLeft;
            state.bayCenterMm = preset.defaultBayCenter;
            state.bayRightMm = preset.defaultBayRight;
            if (bayLeftInput) bayLeftInput.value = String(state.bayLeftMm);
            if (bayCenterInput) bayCenterInput.value = String(state.bayCenterMm);
            if (bayRightInput) bayRightInput.value = String(state.bayRightMm);
          }
          if (preset.defaultCornerLeft) {
            state.cornerLeftMm = preset.defaultCornerLeft;
            state.cornerRightMm = preset.defaultCornerRight;
            if (cornerLeftInput) cornerLeftInput.value = String(state.cornerLeftMm);
            if (cornerRightInput) cornerRightInput.value = String(state.cornerRightMm);
          }
        }
      }
      if (params.get('fabric')) state.primaryFabricId = params.get('fabric');
      if (params.get('swatch')) state.primarySwatchId = params.get('swatch');
      if (params.get('lining')) state.liningType = params.get('lining');
      if (params.get('lighting')) state.lighting = params.get('lighting');
      if (params.get('sample')) {
        state.arSampleId = params.get('sample');
        const defaultC = AR_SAMPLE_DEFAULT_CORNERS[state.arSampleId] || AR_SAMPLE_DEFAULT_CORNERS['living-room'];
        state.arCorners = cloneCorners(defaultC);
      }
      if (params.get('track')) state.trackSeriesId = params.get('track');
      if (params.get('draw')) state.drawPercent = Number(params.get('draw')) || 78;
      if (drawSlider) drawSlider.value = String(state.drawPercent);
      if (state.pleatId === 'roman-blind') state.layerMode = 'single';

      // Optional multi-window schedule demo prefill (?schedule=demo)
      if (params.get('schedule') === 'demo') {
        const savedPreset = state.windowPreset;
        const savedWidth = state.widthMm;
        const savedDrop = state.dropMm;
        const savedBends = state.bendsCount;
        state.windowLabel = 'Window 1 — Living Room Sliding Door';
        state.windowPreset = 'sliding-door';
        state.widthMm = 3600;
        state.dropMm = 2700;
        state.bendsCount = 0;
        state.windowSchedule.push(snapshotCurrentWindowItem());

        state.windowLabel = 'Window 2 — Master 3-Sided Bay Window';
        state.windowPreset = 'bay-window';
        state.stacking = 'centre-open';
        state.bayLeftMm = 950;
        state.bayCenterMm = 2000;
        state.bayRightMm = 950;
        state.widthMm = 3900;
        state.dropMm = 2500;
        state.bendsCount = 2;
        state.windowSchedule.push(snapshotCurrentWindowItem());

        state.windowPreset = savedPreset;
        state.widthMm = savedWidth;
        state.dropMm = savedDrop;
        state.bendsCount = savedBends;
        state.windowLabel = 'Window 3 — Corner Lounge';
        if (windowLabelInput) windowLabelInput.value = state.windowLabel;
      }
    } catch {}

    // Initial render
    renderPleatCards();
    renderTrackFinishes();
    populateFabricSelects();
    updateAll();

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('modal') === 'quote') {
        openQuoteBtn?.click();
      }
    } catch {}
  }

  function initAll() {
    if (typeof document === 'undefined') return;
    document.querySelectorAll('#ll-curtain-studio-root').forEach(mountStudio);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
  }

  return {
    STUDIO_EX_GST_FACTOR,
    GST_MULTIPLIER,
    STUDIO_RATE_FACTOR,
    TRACK_TABLES,
    FABRIC_RANGES,
    PLEAT_STYLES,
    WINDOW_PRESETS,
    calculateTrackBase,
    calculateCurtainLayerBase,
    calculateRomanBlindBase,
    calculateCompletePricing,
    renderStudioCanvas,
    mountStudio,
    initAll
  };
});
